import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, FormEvent, MouseEvent, ReactNode } from "react";
import type {
  AdminInventoryRow,
  AdminUser,
  Cart,
  Category,
  ChangePasswordResponse,
  Customer,
  CustomerAddress,
  DeliveryDate,
  EmailTemplate,
  Order,
  OrderDetail,
  OrderStatus,
  PackingDashboard,
  ProductDetail,
  ProductImage,
  ProductStatus,
  ProductSummary,
  ProductVariant,
  PackDayCapacity,
  ShipmentGroup,
  ShippingOption,
  ShippingSettings,
  StorefrontSettings,
  VariantStatus
} from "../shared/types";

type ProductsResponse = { products: ProductSummary[] };
type ProductResponse = { product: ProductDetail };
type CartResponse = { cart: Cart };
type CustomerResponse = { customer: Customer | null };
type CustomerStartResponse = { mode: "LOGIN_REQUIRED" | "CREATED"; email?: string; has_password?: number; customer?: Customer };
type CustomerAddressesResponse = { addresses: CustomerAddress[] };
type CustomerAddressResponse = { address: CustomerAddress };
type InventoryResponse = { inventory: AdminInventoryRow[] };
type CategoryWithCount = Category & { created_at: number; product_count: number };
type CategoriesResponse = { categories: CategoryWithCount[] };
type DeliveryDatesResponse = { delivery_dates: DeliveryDate[] };
type ShippingSettingsResponse = { settings: ShippingSettings };
type StorefrontSettingsResponse = { settings: StorefrontSettings };
type PackDayCapacityResponse = { capacities: PackDayCapacity[]; day_names: string[] };
type ShipmentGroupsResponse = { shipment_groups: ShipmentGroup[] };
type ShippingOptionsResponse = { options: ShippingOption[]; messages: string[] };
type OrdersResponse = { orders: Order[] };
type OrderResponse = { order: OrderDetail };
type CheckoutResponse = { checkout_url: string; stripe_checkout_session_id: string };
type PackingResponse = { dashboard: PackingDashboard };
type EmailTemplatesResponse = { templates: EmailTemplate[] };
type EmailTemplateResponse = { template: EmailTemplate };
type EmailPreviewResponse = { preview: { subject: string; body: string; variables: Record<string, string> } };
type AdminProductsResponse = { products: ProductSummary[]; categories: Category[] };
type AdminProductResponse = { product: ProductDetail; categories: Category[] };
type MeResponse = { user: AdminUser | null };

const productStatuses: ProductStatus[] = ["ACTIVE", "DRAFT", "ARCHIVED"];
const variantStatuses: VariantStatus[] = ["ACTIVE", "INACTIVE", "ARCHIVED"];
const orderStatuses: OrderStatus[] = ["DRAFT", "PENDING_PAYMENT", "PAID", "PACKING", "SHIPPED", "DELIVERED", "CANCELLED"];
const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
type StorefrontCategory = {
  name: string;
  slug: string;
  images: string[];
  matchSlugs?: string[];
  sidebarLabel?: string;
};

const storefrontCategories: StorefrontCategory[] = [
  {
    name: "Mbuna & Victorian Cichlids",
    slug: "mbuna-victorian-cichlids-19",
    images: ["/media/legacy-assets/live-categories/e-yellow.jpg"],
    matchSlugs: ["mbuna-victorian-cichlids-19"]
  },
  {
    name: "Peacock & Hap Cichlids",
    slug: "peacock-hap-cichlids-21",
    images: ["/media/legacy-assets/live-categories/peacocks-and-haps.jpg"],
    matchSlugs: ["peacock-hap-cichlids-21", "malawi-peacock-cichlids-20"]
  },
  {
    name: "Tanganyikan Cichlids",
    slug: "tanganyikan-cichlids-22",
    images: ["/media/legacy-assets/live-categories/tangs.jpg"],
    matchSlugs: ["tanganyikan-cichlids-22"]
  },
  {
    name: "South American Cichlids",
    slug: "south-american-cichlids",
    images: ["/media/legacy-assets/live-categories/south-americans.jpg"]
  },
  {
    name: "Bottom Feeders",
    slug: "bottom-feeders",
    images: ["/media/legacy-assets/live-categories/bottom-feeders.jpg"]
  },
  {
    name: "Fish Food",
    slug: "fish-food",
    images: ["/media/legacy-assets/live-categories/food.jpg"]
  },
  {
    name: "Community Fish Medium+",
    slug: "community-fish-medium",
    images: ["/media/legacy-assets/live-categories/boesemani-rainbow.png"],
    sidebarLabel: "Community Fish Medium+"
  },
  {
    name: "Sm Community",
    slug: "small-community-fish",
    images: ["/media/legacy-assets/live-categories/endler-guppy.png"],
    sidebarLabel: "Sm Community (Free Ship on $100+)"
  },
  {
    name: "Invertebrates",
    slug: "invertebrates",
    images: ["/media/legacy-assets/live-categories/neo-davidi.jpg"]
  },
  {
    name: "Special Price",
    slug: "special-price",
    images: ["/media/legacy-assets/live-categories/e-yellow.jpg"]
  },
  {
    name: "New",
    slug: "new",
    images: ["/media/legacy-assets/live-categories/peacocks-and-haps.jpg"]
  },
  {
    name: "Recommended",
    slug: "recommended",
    images: ["/media/legacy-assets/live-categories/tangs.jpg"]
  }
];

const mainNavItems = [
  { label: "Home", path: "/" },
  { label: "Categories", path: "/category/mbuna-victorian-cichlids-19" },
  { label: "FAQ", path: "/" },
  { label: "Facilities", path: "/" },
  { label: "Blog", path: "/" },
  { label: "Contact Us", path: "/" },
  { label: "About Us", path: "/" }
];

const defaultStorefrontSettings: StorefrontSettings = {
  homepage_category_order: storefrontCategories.map((category) => category.slug),
  homepage_category_visibility: {
    "special-price": false,
    new: false,
    recommended: false
  },
  homepage_logo_size: 900,
  homepage_blurb:
    "Welcome to Live Fish Direct, where you can shop live African cichlids and other freshwater tropical aquarium fish direct from our farm. Choose a category above to browse current inventory, sizes, and availability.",
  homepage_ups_image_url: "/media/legacy-assets/USP-Logo.png",
  homepage_category_images: {},
  category_image_rotation_enabled: 0,
  category_image_rotation_interval: 8
};

function formatPrice(priceCents: number | null) {
  if (priceCents === null) return "Not priced";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(priceCents / 100);
}

function dollars(priceCents: number) {
  return (priceCents / 100).toFixed(2);
}

function centsFromInput(value: string) {
  return Math.round(Number(value || "0") * 100);
}

function shiftDate(value: string, days: number) {
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function makeSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getStockState(available: number) {
  if (available <= 0) return "out";
  if (available <= 5) return "low";
  return "in";
}

function getStockLabel(available: number) {
  const state = getStockState(available);
  if (state === "out") return "Out of stock";
  if (state === "low") return "Low stock";
  return "In stock";
}

const cartUpdatedEventName = "cart-updated";
const storeSearchEventName = "store-search-updated";
let currentStoreSearch = "";
type CartUpdatedDetail = { itemCount: number; subtotalCents: number };

function notifyCartUpdated(cart?: Cart) {
  window.dispatchEvent(
    new CustomEvent<CartUpdatedDetail | undefined>(cartUpdatedEventName, {
      detail: cart ? { itemCount: cart.item_count, subtotalCents: cart.subtotal_cents } : undefined
    })
  );
}

function updateStoreSearch(value: string) {
  currentStoreSearch = value;
  window.dispatchEvent(new CustomEvent<string>(storeSearchEventName, { detail: value }));
}

function useStoreSearch(setSearch: (value: string) => void) {
  useEffect(() => {
    setSearch(currentStoreSearch);
    const onSearch = (event: Event) => setSearch(String((event as CustomEvent<string>).detail ?? ""));
    window.addEventListener(storeSearchEventName, onSearch);
    return () => window.removeEventListener(storeSearchEventName, onSearch);
  }, [setSearch]);
}

function storefrontCategoryForSlug(slug: string) {
  return storefrontCategories.find((category) => category.slug === slug || category.matchSlugs?.includes(slug));
}

function categoryImages(category: Category | CategoryWithCount | StorefrontCategory) {
  if ("images" in category) return category.images;
  return storefrontCategoryForSlug(category.slug)?.images ?? ["/media/legacy-assets/category-haps.jpg"];
}

function orderedStorefrontCategories(settings: StorefrontSettings) {
  const bySlug = new Map(storefrontCategories.map((category) => [category.slug, category]));
  const ordered = settings.homepage_category_order
    .map((slug) => bySlug.get(slug))
    .filter((category): category is StorefrontCategory => Boolean(category));
  for (const category of storefrontCategories) {
    if (!ordered.some((item) => item.slug === category.slug)) ordered.push(category);
  }
  return ordered.filter((category) => settings.homepage_category_visibility[category.slug] !== false);
}

function storefrontCategoryImages(category: StorefrontCategory, settings: StorefrontSettings) {
  const override = settings.homepage_category_images[category.slug]?.trim();
  return override ? [override, ...category.images] : category.images;
}

function useStorefrontSettings() {
  const [settings, setSettings] = useState<StorefrontSettings>(defaultStorefrontSettings);

  useEffect(() => {
    fetch("/api/storefront-settings")
      .then((response) => (response.ok ? (response.json() as Promise<StorefrontSettingsResponse>) : null))
      .then((data) => setSettings(data?.settings ?? defaultStorefrontSettings))
      .catch(() => setSettings(defaultStorefrontSettings));
  }, []);

  return settings;
}

function productMatchesSearch(product: ProductSummary, search: string) {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [product.name, product.category?.name, product.slug, product.description]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
}

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return pathname;
}

function goTo(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigate(event: MouseEvent<HTMLAnchorElement>) {
  const href = event.currentTarget.getAttribute("href");
  if (!href || href.startsWith("http")) return;
  event.preventDefault();
  goTo(href);
}

type LogoLedStyle = CSSProperties & {
  "--lfd-logo-led-gradient": string;
};

const logoLedBandColors = ["#69e5ff", "#a568ff", "#6fffd0", "#ffe76b", "#ff9c36", "#ff6254"];

function createLogoLedStyle(): LogoLedStyle {
  const colorStart = 38;
  const colorEnd = 70;
  const transitionWidth = 1.1;
  const usableWidth = colorEnd - colorStart - transitionWidth * (logoLedBandColors.length - 1);
  const weights = logoLedBandColors.map(() => 0.7 + Math.random() * 2.6);
  const totalWeight = weights.reduce((total, weight) => total + weight, 0);
  const stops = ["#ffffff 0%", "#ffffff 36%"];
  let cursor = colorStart;

  logoLedBandColors.forEach((color, index) => {
    const bandWidth = (weights[index] / totalWeight) * usableWidth;
    const bandEnd = cursor + bandWidth;
    stops.push(`${color} ${cursor.toFixed(1)}%`, `${color} ${bandEnd.toFixed(1)}%`);
    cursor = bandEnd + transitionWidth;
  });

  stops.push("#ffffff 72%", "#ffffff 100%");
  return { "--lfd-logo-led-gradient": `linear-gradient(90deg, ${stops.join(", ")})` };
}

function AdminNav({ user }: { user?: AdminUser }) {
  const [cartCount, setCartCount] = useState(0);
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState(currentStoreSearch);
  const logoLedStyle = useMemo(createLogoLedStyle, []);

  useEffect(() => {
    const loadCartCount = () => {
      fetch("/api/cart")
        .then((response) => response.json() as Promise<CartResponse>)
        .then((data) => {
          setCartCount(data.cart.item_count);
          setCartSubtotal(data.cart.subtotal_cents);
        })
        .catch(() => {
          setCartCount(0);
          setCartSubtotal(0);
        });
    };
    const onCartUpdated = (event: Event) => {
      const detail = (event as CustomEvent<CartUpdatedDetail | undefined>).detail;
      if (detail) {
        setCartCount(detail.itemCount);
        setCartSubtotal(detail.subtotalCents);
      }
      loadCartCount();
    };

    loadCartCount();
    window.addEventListener(cartUpdatedEventName, onCartUpdated);
    return () => window.removeEventListener(cartUpdatedEventName, onCartUpdated);
  }, []);

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" });
    goTo("/admin/login");
  }

  function changeSearch(value: string) {
    setSearch(value);
    updateStoreSearch(value);
    if (value.trim() && window.location.pathname !== "/" && !window.location.pathname.startsWith("/category/")) {
      goTo("/");
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    if (window.location.pathname !== "/" && !window.location.pathname.startsWith("/category/")) goTo("/");
    setSearchOpen(false);
  }

  function menuNavigate(event: MouseEvent<HTMLAnchorElement>) {
    setMenuOpen(false);
    setSearchOpen(false);
    navigate(event);
  }

  return (
    <>
      <header className="site-header">
        <div className="site-header-main">
          <button
            aria-expanded={menuOpen}
            aria-label="Mobile Menu"
            className="menu-toggle"
            onClick={() => setMenuOpen(true)}
            type="button"
          >
            <span aria-hidden="true" className="dashicon dashicon-menu" />
          </button>
          <a className="nav-logo" href="/" onClick={navigate} style={logoLedStyle} aria-label="LiveFishDirect home">
            <img alt="LiveFishDirect" src="/media/legacy-assets/live-logo-led-mask-hidpi.png" />
          </a>
          <form className={searchOpen ? "header-search search-open" : "header-search"} onSubmit={submitSearch} role="search">
            <div className="header-search-fields">
              <input
                aria-label="Search Fish"
                placeholder="Search Fish"
                type="search"
                value={search}
                onChange={(event) => changeSearch(event.target.value)}
              />
              <button aria-label="Search" className="search-submit" type="submit">
                <span aria-hidden="true" className="dashicon dashicon-search" />
              </button>
            </div>
          </form>
          <div className="header-actions">
            <a className="account-link" href="/orders" onClick={navigate}>
              <span aria-hidden="true" className="dashicon dashicon-account" />
              <span>
                <strong>My Account</strong>
              </span>
            </a>
            <a className="wishlist-link" href="/" onClick={navigate} aria-label="Wishlist">
              <span aria-hidden="true" className="dashicon dashicon-heart" />
            </a>
            <button
              aria-expanded={searchOpen}
              aria-label="Search Toggle"
              className="mobile-search-toggle"
              onClick={() => setSearchOpen((open) => !open)}
              type="button"
            >
              <span aria-hidden="true" className="dashicon dashicon-search" />
            </button>
            <a className="cart-nav-link" href="/cart" onClick={navigate} aria-label={`Cart with ${cartCount} items`}>
              <span aria-hidden="true" className="dashicon dashicon-cart" />
              <strong>{cartCount}</strong>
              <span className="cart-subtotal">{formatPrice(cartSubtotal)}</span>
            </a>
          </div>
        </div>
        <nav className="admin-nav" aria-label="Site navigation">
          {mainNavItems.map((item) => (
            <a href={item.path} key={item.label} onClick={menuNavigate}>
              {item.label}
            </a>
          ))}
        </nav>
      </header>

      <div className={menuOpen ? "mobile-menu-overlay active" : "mobile-menu-overlay"} onClick={() => setMenuOpen(false)} />
      <aside className={menuOpen ? "mobile-menu-panel open" : "mobile-menu-panel"} aria-label="Mobile Menu">
        <button aria-label="Close the Mobile Menu" className="mobile-menu-close" onClick={() => setMenuOpen(false)} type="button">
          <span />
          <span />
        </button>
        <div className="mobile-tabs">
          <span className="active">Main Menu</span>
          <span>Top Navigation</span>
        </div>
        <nav className="mobile-menu-links">
          {mainNavItems.map((item) => (
            <a href={item.path} key={item.label} onClick={menuNavigate}>
              {item.label}
            </a>
          ))}
          <details open>
            <summary>Categories</summary>
            {storefrontCategories.map((category) => (
              <a href={`/category/${category.slug}`} key={category.slug} onClick={menuNavigate}>
                {category.sidebarLabel ?? category.name}
              </a>
            ))}
          </details>
          <a href="/orders" onClick={menuNavigate}>
            Orders
          </a>
          {user ? (
            <>
              <a href="/admin/products" onClick={menuNavigate}>Admin Products</a>
              <a href="/admin/inventory" onClick={menuNavigate}>Inventory</a>
              <a href="/admin/categories" onClick={menuNavigate}>Admin Categories</a>
              <a href="/admin/orders" onClick={menuNavigate}>Admin Orders</a>
              <a href="/admin/pages" onClick={menuNavigate}>Admin Pages</a>
              <button className="secondary" onClick={logout} type="button">
                Logout {user.email}
              </button>
            </>
          ) : null}
        </nav>
      </aside>
    </>
  );
}

function StoreFooter() {
  return (
    <footer className="store-footer">
      <div>
        <img alt="LiveFishDirect" src="/media/legacy-assets/white-logo.png" />
        <p>Freshwater fish, carefully packed for arrival day.</p>
      </div>
      <div>
        <strong>Shop</strong>
        <a href="/" onClick={navigate}>Current Stock</a>
        <a href="/cart" onClick={navigate}>Cart</a>
        <a href="/orders" onClick={navigate}>Orders</a>
      </div>
      <div>
        <strong>Service</strong>
        <span>14 day arrive healthy guarantee</span>
        <span>$55 flat-rate shipping foundation</span>
        <span>Delivery Monday-Thursday</span>
      </div>
      <div>
        <strong>Contact</strong>
        <span>14551 S. 790 W. Bluffdale Utah 84065</span>
        <span>(801) 572-2009</span>
        <span>orders@livefishdirect.com</span>
      </div>
    </footer>
  );
}

function TrustSections() {
  return (
    <section className="service-promos" aria-label="LiveFishDirect service promises">
      <article>
        <span aria-hidden="true" className="service-icon service-shipping" />
        <strong>Overnight Shipping</strong>
      </article>
      <article>
        <span aria-hidden="true" className="service-icon service-check" />
        <strong>Healthy Fish Delivery</strong>
      </article>
    </section>
  );
}

function HomeCategorySidebar({ settings = defaultStorefrontSettings }: { settings?: StorefrontSettings }) {
  const categories = orderedStorefrontCategories(settings);

  return (
    <aside className="home-category-sidebar" aria-label="Product Categories">
      <h2><span aria-hidden="true">&gt;</span> Product Categories</h2>
      <nav>
        {categories.map((category) => (
          <a href={`/category/${category.slug}`} key={category.slug} onClick={navigate}>
            {category.sidebarLabel ?? category.name}
          </a>
        ))}
        <a href="/" onClick={navigate}>Gift Certificates</a>
      </nav>
    </aside>
  );
}

function CategoryShowcase({ categories, settings }: { categories: CategoryWithCount[]; settings: StorefrontSettings }) {
  const categoryCounts = new Map(categories.map((category) => [category.slug, category.product_count]));
  const productCountFor = (category: StorefrontCategory) =>
    [category.slug, ...(category.matchSlugs ?? [])].reduce((count, slug) => count + (categoryCounts.get(slug) ?? 0), 0);
  const displayCategories = orderedStorefrontCategories(settings);

  return (
    <section className="category-showcase" aria-label="Shop categories">
      {displayCategories.map((category) => (
        <a className="category-card" href={`/category/${category.slug}`} key={category.slug} onClick={navigate}>
          <div
            className={settings.category_image_rotation_enabled ? "category-card-media rotating" : "category-card-media"}
            style={{ "--rotation-interval": `${settings.category_image_rotation_interval}s` } as CSSProperties}
          >
            {storefrontCategoryImages(category, settings).map((image) => (
              <img alt="" key={image} src={image} />
            ))}
          </div>
          <span>{productCountFor(category)} products</span>
          <strong>{category.name}</strong>
        </a>
      ))}
    </section>
  );
}

const matrixRows = ["Juvenile", "Premium", "Young adult", "Adult"];
const matrixColumns = ["Male", "Female", "Unsexed"];

function variantSizeLabel(variant: ProductVariant) {
  const value = `${variant.fish_size ?? ""} ${variant.name}`.toLowerCase();
  if (value.includes("juvenile")) return "Juvenile";
  if (value.includes("premium")) return "Premium";
  if (value.includes("young")) return "Young adult";
  if (value.includes("adult")) return "Adult";
  return variant.fish_size ?? "Premium";
}

function variantSexLabel(variant: ProductVariant) {
  const value = `${variant.sex ?? ""} ${variant.name}`.toLowerCase();
  if (value.includes("female")) return "Female";
  if (value.includes("male")) return "Male";
  return "Unsexed";
}

function AvailabilityMatrix({ product, detail }: { product: ProductSummary; detail?: ProductDetail }) {
  const variants = detail?.variants ?? [];
  const rows = [...matrixRows];
  for (const variant of variants) {
    const row = variantSizeLabel(variant);
    if (!rows.includes(row)) rows.push(row);
  }

  return (
    <table className="availability-matrix" aria-label={`${product.name} availability`}>
      <thead>
        <tr>
          <th>Size</th>
          {matrixColumns.map((column) => (
            <th key={column}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row}>
            <td>{row}</td>
            {matrixColumns.map((column) => {
              const available = variants.some(
                (variant) =>
                  variant.status === "ACTIVE" &&
                  variant.available_quantity > 0 &&
                  variantSizeLabel(variant) === row &&
                  variantSexLabel(variant) === column
              );
              return (
                <td key={column}>
                  {available ? <span aria-label="Available" className="matrix-dot" /> : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProductGrid({ products, details = {} }: { products: ProductSummary[]; details?: Record<string, ProductDetail> }) {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  function productQuantity(product: ProductSummary) {
    return quantities[product.id] ?? 1;
  }

  function setProductQuantity(product: ProductSummary, quantity: number) {
    const max = product.quick_add_available_quantity || 1;
    setQuantities({ ...quantities, [product.id]: Math.min(max, Math.max(1, quantity)) });
  }

  async function quickAdd(product: ProductSummary) {
    if (!product.quick_add_variant_id) return;
    const quantity = productQuantity(product);
    setSaving({ ...saving, [product.id]: true });
    setMessages({ ...messages, [product.id]: "" });
    const response = await fetch("/api/cart/items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variant_id: product.quick_add_variant_id, quantity })
    });
    setSaving({ ...saving, [product.id]: false });

    if (!response.ok) {
      setMessages({ ...messages, [product.id]: await readApiError(response) });
      return;
    }

    const data = (await response.json()) as CartResponse;
    setMessages({ ...messages, [product.id]: `Added ${quantity} to cart.` });
    notifyCartUpdated(data.cart);
  }

  return (
    <section className="product-list" aria-label="Products">
      {products.map((product) => {
        const detail = details[product.slug];
        return (
        <article className="product-card inventory-product-card" key={product.id}>
          <a className="product-card-link" href={`/product/${product.slug}`} onClick={navigate}>
            <span className="product-card-image">
              {product.image_url ? (
                <img alt={product.image_alt_text ?? product.name} src={product.image_url} />
              ) : (
                <span>No image</span>
              )}
            </span>
            <span className="product-card-category">{product.category?.name ?? "Uncategorized"}</span>
            <h2>{product.name}</h2>
            <span className={`archive-stock stock-${getStockState(product.available_quantity)}`}>
              {product.available_quantity > 0 ? "In Stock" : "Out of Stock"}
            </span>
          </a>
          <AvailabilityMatrix product={product} detail={detail} />
          {product.quick_add_variant_id ? (
            <div className="quick-add" aria-label={`Quick add ${product.name}`}>
              <span>{formatPrice(product.min_price_cents)} from {product.quick_add_variant_name ?? "available option"}</span>
              <div className="quantity-stepper">
                <button
                  aria-label={`Decrease ${product.name} quantity`}
                  className="stepper-button"
                  onClick={() => setProductQuantity(product, productQuantity(product) - 1)}
                  type="button"
                >
                  -
                </button>
                <output>{productQuantity(product)}</output>
                <button
                  aria-label={`Increase ${product.name} quantity`}
                  className="stepper-button"
                  onClick={() => setProductQuantity(product, productQuantity(product) + 1)}
                  type="button"
                >
                  +
                </button>
              </div>
              <button
                className="quick-add-button"
                disabled={saving[product.id]}
                onClick={() => quickAdd(product)}
                type="button"
              >
                {saving[product.id] ? "Adding" : "Add"}
              </button>
            </div>
          ) : (
            <a className="select-options" href={`/product/${product.slug}`} onClick={navigate}>
              Select options
            </a>
          )}
          {messages[product.id] ? (
            <p className={messages[product.id].startsWith("Added") ? "quick-add-message success-text" : "quick-add-message error-text"}>
              {messages[product.id]}
            </p>
          ) : null}
        </article>
        );
      })}
    </section>
  );
}

async function readApiError(response: Response) {
  try {
    const data = (await response.json()) as { error?: string };
    return data.error ?? "Request failed";
  } catch {
    return "Request failed";
  }
}

function AuthGate({ children }: { children: (user: AdminUser) => ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((response) => {
        if (!response.ok) {
          goTo("/admin/login");
          return null;
        }
        return response.json() as Promise<MeResponse>;
      })
      .then((data) => setUser(data?.user ?? null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <main className="page">Checking admin session...</main>;
  if (!user) return null;

  return <>{children(user)}</>;
}

function LoginPage() {
  const [email, setEmail] = useState("admin@livefishdirect.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function login(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    setSaving(false);

    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }

    goTo("/admin/products");
  }

  return (
    <main className="page login-page">
      <header className="product-header">
        <p className="eyebrow">Admin</p>
        <h1>Login</h1>
        <p>Sign in to manage products, variants, images, and inventory.</p>
      </header>
      {error ? <p className="error">{error}</p> : null}
      <form className="form-grid login-form" onSubmit={login}>
        <label>
          Email
          <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <label>
          Password
          <input required type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <button disabled={saving} type="submit">
          {saving ? "Logging in" : "Login"}
        </button>
      </form>
    </main>
  );
}

function HomePage() {
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const storefrontSettings = useStorefrontSettings();

  useEffect(() => {
    fetch("/api/categories")
      .then((response) => response.json() as Promise<CategoriesResponse>)
      .then((data) => setCategories(data.categories))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="page">
      <AdminNav />
      <header className="page-header">
        <div className="home-brand">
          <img
            alt="LiveFishDirect"
            src="/media/legacy-assets/logo1-transparent.png"
            style={{ "--homepage-logo-size": `${storefrontSettings.homepage_logo_size}px` } as CSSProperties}
          />
        </div>
      </header>

      {loading ? (
        <p className="muted">Loading products...</p>
      ) : (
        <div className="home-storefront-layout">
          <HomeCategorySidebar settings={storefrontSettings} />
          <div className="home-storefront-main">
            <CategoryShowcase categories={categories} settings={storefrontSettings} />
            <section className="home-copy">
              <p>{storefrontSettings.homepage_blurb}</p>
              {storefrontSettings.homepage_ups_image_url ? (
                <div className="home-shipping-graphic">
                  <img alt="UPS" src={storefrontSettings.homepage_ups_image_url} />
                  <span>Overnight delivery through UPS for live fish orders.</span>
                </div>
              ) : null}
            </section>
          </div>
        </div>
      )}
    </main>
  );
}

function CategoryPage({ slug }: { slug: string }) {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [details, setDetails] = useState<Record<string, ProductDetail>>({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const storefrontSettings = useStorefrontSettings();

  useEffect(() => {
    Promise.all([
      fetch("/api/products").then((response) => response.json() as Promise<ProductsResponse>),
      fetch("/api/categories").then((response) => response.json() as Promise<CategoriesResponse>)
    ])
      .then(([productsData, categoriesData]) => {
        setProducts(productsData.products);
        setCategories(categoriesData.categories);
      })
      .finally(() => setLoading(false));
  }, []);
  useStoreSearch(setSearch);

  const storefrontCategory = storefrontCategoryForSlug(slug);
  const category = categories.find((item) => item.slug === slug || storefrontCategory?.matchSlugs?.includes(item.slug));
  const visibleProducts = products
    .filter((product) => product.category?.slug === slug || storefrontCategory?.matchSlugs?.includes(product.category?.slug ?? ""))
    .filter((product) => productMatchesSearch(product, search));
  const displayCategoryName = storefrontCategory?.name ?? category?.name ?? "Category";

  useEffect(() => {
    const missing = visibleProducts.filter((product) => !details[product.slug]).slice(0, 24);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((product) =>
        fetch(`/api/products/${product.slug}`)
          .then((response) => (response.ok ? (response.json() as Promise<ProductResponse>) : null))
          .then((data) => data?.product)
      )
    ).then((items) => {
      const nextDetails = { ...details };
      for (const item of items) {
        if (item) nextDetails[item.slug] = item;
      }
      setDetails(nextDetails);
    });
  }, [visibleProducts.map((product) => product.slug).join("|")]);

  if (loading) return <main className="page"><AdminNav /><p>Loading category...</p></main>;

  if (!category && !storefrontCategory) {
    return (
      <main className="page">
        <AdminNav />
        <a href="/" onClick={navigate}>Back to categories</a>
        <h1>Category not found</h1>
      </main>
    );
  }

  return (
    <main className="page">
      <AdminNav />
      <header className="category-hero">
        <div className="category-hero-media">
          {categoryImages(storefrontCategory ?? category!).map((image) => (
            <img alt="" key={image} src={image} />
          ))}
        </div>
        <div>
          <p className="eyebrow">Shop category</p>
          <h1>{displayCategoryName}</h1>
          <p>
            {visibleProducts.length > 0
              ? `${visibleProducts.length} products in the current LFD2 staging sample.`
              : "Representative category landing page ready for the full catalog import."}
          </p>
        </div>
      </header>
      <div className="home-storefront-layout">
        <HomeCategorySidebar settings={storefrontSettings} />
        <div className="home-storefront-main">
          <div className="category-toolbar">
            <label>
              Sort by
              <select defaultValue="default">
                <option value="default">Default sorting</option>
                <option value="name">Name</option>
                <option value="price">Price</option>
              </select>
            </label>
            <div className="view-toggles" aria-label="View options">
              <button className="secondary active-filter" type="button">Grid</button>
              <button className="secondary" type="button">List</button>
            </div>
          </div>
          <div className="filter-bar" aria-label="Category navigation">
            <a className="button-link" href="/" onClick={navigate}>All categories</a>
            {categories.map((item) => (
              <a
                className={item.slug === slug ? "button-link active-link" : "button-link secondary-link"}
                href={`/category/${item.slug}`}
                key={item.id}
                onClick={navigate}
              >
                {item.name}
              </a>
            ))}
          </div>
          <ProductGrid products={visibleProducts} details={details} />
        </div>
      </div>
    </main>
  );
}

function ProductPage({ slug }: { slug: string }) {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [cartMessages, setCartMessages] = useState<Record<string, string>>({});
  const storefrontSettings = useStorefrontSettings();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/products/${slug}`)
      .then((response) => {
        if (!response.ok) throw new Error("Product not found");
        return response.json() as Promise<ProductResponse>;
      })
      .then((data) => setProduct(data.product))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    fetch("/api/products")
      .then((response) => response.json() as Promise<ProductsResponse>)
      .then((data) => setRelatedProducts(data.products.filter((item) => item.slug !== slug).slice(0, 3)))
      .catch(() => setRelatedProducts([]));
  }, [slug]);

  if (loading) return <main className="page">Loading product...</main>;

  if (!product) {
    return (
      <main className="page">
        <a href="/" onClick={navigate}>
          Back to products
        </a>
        <h1>Product not found</h1>
      </main>
    );
  }

  async function addToCart(variant: ProductVariant) {
    const quantity = quantities[variant.id] ?? 1;
    setCartMessages({ ...cartMessages, [variant.id]: "" });
    const response = await fetch("/api/cart/items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variant_id: variant.id, quantity })
    });

    if (!response.ok) {
      setCartMessages({ ...cartMessages, [variant.id]: await readApiError(response) });
      return;
    }

    const data = (await response.json()) as CartResponse;
    setCartMessages({ ...cartMessages, [variant.id]: "Added to cart." });
    notifyCartUpdated(data.cart);
  }

  return (
    <main className="page">
      <AdminNav />
      <div className="product-template-layout">
        <HomeCategorySidebar settings={storefrontSettings} />
        <div className="product-template-main">
          <a className="back-link" href={`/category/${product.category?.slug ?? "mbuna-victorian-cichlids-19"}`} onClick={navigate}>
            Back to {product.category?.name ?? "categories"}
          </a>
          <h1 className="product-title">{product.name}</h1>
          <section className="product-hero-panel">
            <figure className="product-primary-image">
              {product.images[0] ? (
                <img
                  alt={product.images[0].alt_text ?? product.name}
                  src={product.images[0].image_url}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : product.image_url ? (
                <img alt={product.image_alt_text ?? product.name} src={product.image_url} />
              ) : (
                <span>No image</span>
              )}
            </figure>
            <div className="product-summary-panel">
              <h2>{product.name}</h2>
              <p>{product.description}</p>
              <dl className="product-meta-list">
                <div><dt>Categories</dt><dd>{product.category?.name ?? "Uncategorized"}</dd></div>
                <div><dt>Status</dt><dd>{product.status}</dd></div>
                <div><dt>Available</dt><dd>{product.available_quantity}</dd></div>
              </dl>
            </div>
          </section>

          <section className="product-purchase-panel">
            <table className="purchase-matrix">
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Color</th>
                  <th>Stock</th>
                  <th>Price</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {product.variants.map((variant) => (
                  <tr key={variant.id}>
                    <td>
                      {variant.fish_size ?? variant.name}
                      {variant.length_min || variant.length_max ? (
                        <span>{variant.length_min ?? "?"}-{variant.length_max ?? "?"} in</span>
                      ) : null}
                    </td>
                    <td>{variant.name}</td>
                    <td>{variant.available_quantity} in stock</td>
                    <td>{formatPrice(variant.price_cents)}</td>
                    <td>
                      <div className="product-add-row">
                        <input
                          min="1"
                          max={variant.available_quantity}
                          type="number"
                          value={quantities[variant.id] ?? 1}
                          onChange={(event) =>
                            setQuantities({
                              ...quantities,
                              [variant.id]: Math.max(1, event.target.valueAsNumber || 1)
                            })
                          }
                        />
                        <button
                          disabled={variant.status !== "ACTIVE" || variant.available_quantity <= 0}
                          onClick={() => addToCart(variant)}
                          type="button"
                        >
                          {variant.available_quantity <= 0 ? "Out" : "Add to Cart"}
                        </button>
                      </div>
                      {cartMessages[variant.id] ? (
                        <p className={cartMessages[variant.id] === "Added to cart." ? "success-text" : "error-text"}>
                          {cartMessages[variant.id]}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="product-description-panel">
            <h2>Description</h2>
            <p>{product.description}</p>
            <details open>
              <summary>General Information & Care</summary>
              <p>Fish are packed for safe arrival and should be acclimated carefully on delivery day.</p>
            </details>
            <details>
              <summary>Additional Information & Care</summary>
              <p>Tank mates, diet, and water conditions vary by species. Contact LiveFishDirect with care questions.</p>
            </details>
            <details>
              <summary>Want to learn more?</summary>
              <p>More species-specific care content will be restored as the full catalog migration continues.</p>
            </details>
          </section>

          {relatedProducts.length > 0 ? (
            <section className="related-products">
              <h2>Related Products</h2>
              <ProductGrid products={relatedProducts} />
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}

const emptyAddress = {
  label: "Shipping",
  first_name: "",
  last_name: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  state: "UT",
  postal_code: "",
  country: "US",
  county: "",
  is_default: 1
};

function CheckoutSteps({ current }: { current: "cart" | "checkout" | "complete" }) {
  const steps = [
    { key: "cart", label: "Shopping Cart", path: "/cart" },
    { key: "checkout", label: "Checkout", path: "/checkout" },
    { key: "complete", label: "Order Complete", path: "/orders" }
  ];

  return (
    <nav className="checkout-steps" aria-label="Checkout progress">
      {steps.map((step, index) => (
        <span className={current === step.key ? "active" : ""} key={step.key}>
          {index > 0 ? <b aria-hidden="true">&gt;</b> : null}
          <a href={step.path} onClick={navigate}>{step.label}</a>
        </span>
      ))}
    </nav>
  );
}

function StoreCreditPanel() {
  return (
    <section className="store-credit-panel">
      <header>
        <span>Available Store Credit</span>
        <span aria-hidden="true">^</span>
      </header>
      <div className="store-credit-list">
        {[150, 100, 100].map((amount, index) => (
          <article className="store-credit-card" key={`${amount}-${index}`}>
            <strong>${amount} Store Credit</strong>
            <span>STORE_CREDIT_READY</span>
            <small>Never expires</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function CustomerIdentitySection({
  onCustomerChange,
  onAddressSelected
}: {
  onCustomerChange?: (customer: Customer | null) => void;
  onAddressSelected: (address: CustomerAddress) => void;
}) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"START" | "LOGIN" | "SET_PASSWORD" | "AUTHENTICATED">("START");
  const [profile, setProfile] = useState({ first_name: "", last_name: "", phone: "" });
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [addressDraft, setAddressDraft] = useState(emptyAddress);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const syncCustomer = (nextCustomer: Customer | null) => {
    setCustomer(nextCustomer);
    onCustomerChange?.(nextCustomer);
    setMode(nextCustomer ? "AUTHENTICATED" : "START");
    if (nextCustomer) {
      setEmail(nextCustomer.email);
      setProfile({
        first_name: nextCustomer.first_name ?? "",
        last_name: nextCustomer.last_name ?? "",
        phone: nextCustomer.phone ?? ""
      });
    }
  };

  const loadAddresses = () => {
    fetch("/api/customer/addresses")
      .then((response) => (response.ok ? (response.json() as Promise<CustomerAddressesResponse>) : null))
      .then((data) => {
        const nextAddresses = data?.addresses ?? [];
        setAddresses(nextAddresses);
        const defaultAddress = nextAddresses.find((address) => address.is_default === 1) ?? nextAddresses[0];
        if (defaultAddress) onAddressSelected(defaultAddress);
      })
      .catch(() => setAddresses([]));
  };

  useEffect(() => {
    fetch("/api/customer/me")
      .then((response) => (response.ok ? (response.json() as Promise<CustomerResponse>) : null))
      .then((data) => {
        syncCustomer(data?.customer ?? null);
        if (data?.customer) loadAddresses();
      })
      .catch(() => syncCustomer(null));
  }, []);

  async function startCustomer(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    const response = await fetch("/api/customer/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CustomerStartResponse;
    if (data.mode === "CREATED" && data.customer) {
      syncCustomer(data.customer);
      setMessage("Account started.");
      loadAddresses();
      notifyCartUpdated();
      return;
    }
    setMode(data.has_password === 1 ? "LOGIN" : "SET_PASSWORD");
    setMessage(data.has_password === 1 ? "Welcome back. Enter your password." : "Account found. Set your first password.");
  }

  async function login(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/customer/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CustomerResponse;
    syncCustomer(data.customer);
    setPassword("");
    loadAddresses();
    notifyCartUpdated();
  }

  async function setFirstPassword(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/customer/set-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CustomerResponse;
    syncCustomer(data.customer);
    setPassword("");
    setMessage("Password saved.");
    loadAddresses();
    notifyCartUpdated();
  }

  async function logout() {
    await fetch("/api/customer/logout", { method: "POST" });
    syncCustomer(null);
    setAddresses([]);
    setPassword("");
    setMessage("Logged out.");
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/customer/profile", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile)
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CustomerResponse;
    syncCustomer(data.customer);
    setMessage("Profile saved.");
  }

  async function saveAddress(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch(editingAddressId ? `/api/customer/addresses/${editingAddressId}` : "/api/customer/addresses", {
      method: editingAddressId ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(addressDraft)
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setAddressDraft(emptyAddress);
    setEditingAddressId(null);
    setMessage("Address saved.");
    loadAddresses();
  }

  async function deleteAddress(addressId: string) {
    setError("");
    const response = await fetch(`/api/customer/addresses/${addressId}`, { method: "DELETE" });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    loadAddresses();
  }

  function editAddress(address: CustomerAddress) {
    setEditingAddressId(address.id);
    setAddressDraft({
      label: address.label ?? "",
      first_name: address.first_name ?? "",
      last_name: address.last_name ?? "",
      phone: address.phone ?? "",
      address1: address.address1 ?? "",
      address2: address.address2 ?? "",
      city: address.city ?? "",
      state: address.state ?? "",
      postal_code: address.postal_code ?? "",
      country: address.country,
      county: address.county ?? "",
      is_default: address.is_default
    });
  }

  return (
    <section className="section-block">
      <h2>Customer</h2>
      {error ? <p className="error">{error}</p> : null}
      {message ? <p className="success">{message}</p> : null}
      {!customer ? (
        <>
          <form className="form-grid customer-form" onSubmit={startCustomer}>
            <label>
              Email
              <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>
            <button disabled={saving} type="submit">
              {saving ? "Continuing" : "Continue"}
            </button>
          </form>
          {mode === "LOGIN" ? (
            <form className="form-grid customer-form" onSubmit={login}>
              <label>
                Password
                <input required type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
              </label>
              <button disabled={saving} type="submit">
                Login
              </button>
            </form>
          ) : null}
          {mode === "SET_PASSWORD" ? (
            <form className="form-grid customer-form" onSubmit={setFirstPassword}>
              <label>
                Set Password
                <input
                  minLength={12}
                  required
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
              <button disabled={saving} type="submit">
                Set Password
              </button>
            </form>
          ) : null}
        </>
      ) : (
        <>
          <div className="editor-card">
            <div className="actions">
              <strong>{customer.first_name || customer.last_name ? `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim() : customer.email}</strong>
              <span className="muted">{customer.email}</span>
              <button className="secondary" onClick={logout} type="button">
                Logout
              </button>
            </div>
            <form className="form-grid compact" onSubmit={saveProfile}>
              <label>
                First Name
                <input value={profile.first_name} onChange={(event) => setProfile({ ...profile, first_name: event.target.value })} />
              </label>
              <label>
                Last Name
                <input value={profile.last_name} onChange={(event) => setProfile({ ...profile, last_name: event.target.value })} />
              </label>
              <label>
                Phone
                <input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} />
              </label>
              <button disabled={saving} type="submit">
                Save Profile
              </button>
            </form>
          </div>
          <div className="editor-card">
            <h3>Shipping / Contact Address</h3>
            {addresses.length > 0 ? (
              <div className="address-list">
                {addresses.map((address) => (
                  <div className="address-row" key={address.id}>
                    <button className="secondary" onClick={() => onAddressSelected(address)} type="button">
                      Use
                    </button>
                    <div>
                      <strong>{address.label || "Address"}</strong>
                      <p className="muted">
                        {[address.address1, address.city, address.state, address.postal_code].filter(Boolean).join(", ")}
                      </p>
                    </div>
                    <div className="actions">
                      <button className="secondary" onClick={() => editAddress(address)} type="button">
                        Edit
                      </button>
                      <button className="danger" onClick={() => deleteAddress(address.id)} type="button">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="notice">No address saved yet.</p>
            )}
            <form className="form-grid compact" onSubmit={saveAddress}>
              <label>
                Label
                <input value={addressDraft.label} onChange={(event) => setAddressDraft({ ...addressDraft, label: event.target.value })} />
              </label>
              <label>
                Phone
                <input value={addressDraft.phone} onChange={(event) => setAddressDraft({ ...addressDraft, phone: event.target.value })} />
              </label>
              <label>
                First Name
                <input value={addressDraft.first_name} onChange={(event) => setAddressDraft({ ...addressDraft, first_name: event.target.value })} />
              </label>
              <label>
                Last Name
                <input value={addressDraft.last_name} onChange={(event) => setAddressDraft({ ...addressDraft, last_name: event.target.value })} />
              </label>
              <label className="span-2">
                Address 1
                <input value={addressDraft.address1} onChange={(event) => setAddressDraft({ ...addressDraft, address1: event.target.value })} />
              </label>
              <label>
                Address 2
                <input value={addressDraft.address2} onChange={(event) => setAddressDraft({ ...addressDraft, address2: event.target.value })} />
              </label>
              <label>
                City
                <input value={addressDraft.city} onChange={(event) => setAddressDraft({ ...addressDraft, city: event.target.value })} />
              </label>
              <label>
                State
                <input
                  maxLength={2}
                  value={addressDraft.state}
                  onChange={(event) => setAddressDraft({ ...addressDraft, state: event.target.value.toUpperCase() })}
                />
              </label>
              <label>
                Postal Code
                <input value={addressDraft.postal_code} onChange={(event) => setAddressDraft({ ...addressDraft, postal_code: event.target.value })} />
              </label>
              <label>
                County
                <input value={addressDraft.county} onChange={(event) => setAddressDraft({ ...addressDraft, county: event.target.value })} />
              </label>
              <label className="checkbox-label">
                <input
                  checked={addressDraft.is_default === 1}
                  type="checkbox"
                  onChange={(event) => setAddressDraft({ ...addressDraft, is_default: event.target.checked ? 1 : 0 })}
                />
                Default
              </label>
              <button disabled={saving} type="submit">
                {editingAddressId ? "Update Address" : "Save Address"}
              </button>
            </form>
          </div>
        </>
      )}
    </section>
  );
}

function CartPage() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [shippingDraft, setShippingDraft] = useState({
    local_pickup_eligible: false,
    destination_state: "UT",
    destination_county: ""
  });
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [shippingMessages, setShippingMessages] = useState<string[]>([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState("");
  const [selectedArrivalDate, setSelectedArrivalDate] = useState("");
  const [draftOrder, setDraftOrder] = useState<OrderDetail | null>(null);
  const [eligibleAddonOrders, setEligibleAddonOrders] = useState<Order[]>([]);
  const [selectedParentOrderId, setSelectedParentOrderId] = useState("");
  const [addonOrder, setAddonOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [orderSaving, setOrderSaving] = useState(false);
  const [addonSaving, setAddonSaving] = useState(false);

  const loadCart = () => {
    fetch("/api/cart")
      .then((response) => response.json() as Promise<CartResponse>)
      .then((data) => {
        setCart(data.cart);
        notifyCartUpdated(data.cart);
      });
  };

  const loadShippingOptions = () => {
    const params = new URLSearchParams({
      contains_live_items: cart && cart.items.length > 0 ? "true" : "false",
      contains_non_live_items: "false",
      destination_state: shippingDraft.destination_state,
      destination_county: shippingDraft.destination_county,
      local_pickup_eligible: shippingDraft.local_pickup_eligible ? "true" : "false"
    });

    fetch(`/api/shipping-options?${params.toString()}`)
      .then((response) => response.json() as Promise<ShippingOptionsResponse>)
      .then((data) => {
        setShippingOptions(data.options);
        setShippingMessages(data.messages);
        setSelectedShippingMethod((current) =>
          current && data.options.some((option) => option.method === current) ? current : (data.options[0]?.method ?? "")
        );
      })
      .catch(() => {
        setShippingOptions([]);
        setShippingMessages(["Shipping options could not be loaded."]);
      });
  };

  const loadAddonEligibleOrders = () => {
    fetch("/api/orders/addon-eligible")
      .then((response) => (response.ok ? (response.json() as Promise<OrdersResponse>) : null))
      .then((data) => {
        const orders = data?.orders ?? [];
        setEligibleAddonOrders(orders);
        setSelectedParentOrderId((current) =>
          current && orders.some((order) => order.id === current) ? current : (orders[0]?.id ?? "")
        );
      })
      .catch(() => setEligibleAddonOrders([]));
  };

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    if (cart && cart.items.length > 0) loadShippingOptions();
  }, [cart?.item_count, shippingDraft]);

  useEffect(() => {
    if (customer) loadAddonEligibleOrders();
  }, [customer?.id]);

  useEffect(() => {
    const option = shippingOptions.find((item) => item.method === selectedShippingMethod);
    setSelectedArrivalDate((current) =>
      current && option?.available_arrival_dates.includes(current) ? current : (option?.available_arrival_dates[0] ?? "")
    );
  }, [selectedShippingMethod, shippingOptions]);

  async function updateItem(itemId: string, quantity: number) {
    setError("");
    const response = await fetch(`/api/cart/items/${itemId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CartResponse;
    setCart(data.cart);
    notifyCartUpdated(data.cart);
  }

  async function removeItem(itemId: string) {
    const response = await fetch(`/api/cart/items/${itemId}`, { method: "DELETE" });
    const data = (await response.json()) as CartResponse;
    setCart(data.cart);
    notifyCartUpdated(data.cart);
  }

  async function clearCart() {
    const response = await fetch("/api/cart", { method: "DELETE" });
    const data = (await response.json()) as CartResponse;
    setCart(data.cart);
    notifyCartUpdated(data.cart);
  }

  async function createDraftOrder() {
    setError("");
    setDraftOrder(null);
    setOrderSaving(true);
    const packDate = selectedShippingMethod === "LOCAL_PICKUP" ? selectedArrivalDate : shiftDate(selectedArrivalDate, -1);
    const response = await fetch("/api/orders/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shipment_method: selectedShippingMethod,
        pack_date: packDate,
        arrival_date: selectedArrivalDate,
        shipping_address_id: selectedAddressId,
        shipping_snapshot: selectedAddressId
          ? null
          : {
              shipping_name: null,
              shipping_phone: null,
              shipping_address1: null,
              shipping_address2: null,
              shipping_city: null,
              shipping_state: shippingDraft.destination_state,
              shipping_postal_code: null,
              shipping_country: "US",
              shipping_county: shippingDraft.destination_county
            }
      })
    });
    setOrderSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as OrderResponse;
    setDraftOrder(data.order);
    loadAddonEligibleOrders();
  }

  async function createAddonOrder() {
    setError("");
    setAddonOrder(null);
    setAddonSaving(true);
    const response = await fetch("/api/orders/addon-draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parent_order_id: selectedParentOrderId })
    });
    setAddonSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as OrderResponse;
    setAddonOrder(data.order);
  }

  return (
    <main className="page wide">
      <AdminNav />
      <CheckoutSteps current="cart" />
      <StoreCreditPanel />

      {error ? <p className="error">{error}</p> : null}
      {!cart ? <p className="muted">Loading cart...</p> : null}
      {cart && cart.items.length === 0 ? <p className="notice">Your cart is empty.</p> : null}
      {cart && cart.items.length > 0 ? (
        <>
          <h1 className="cart-page-title">Shopping Cart</h1>
          <div className="cart-template-layout">
            <section className="cart-template-main">
              <div className="free-shipping-bar">
                <strong>Your order qualifies for free shipping!</strong>
                <span />
              </div>
              <div className="table-wrap cart-table-wrap">
                <table className="cart-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Unit Price</th>
                      <th>Qty</th>
                      <th>Subtotal</th>
                      <th>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <a href={`/product/${item.product_slug}`} onClick={navigate}>
                            {item.product_name}
                          </a>
                          <span>{item.variant_name} / {item.sku}</span>
                        </td>
                        <td>{formatPrice(item.price_cents)}</td>
                        <td>
                          <div className="quantity-stepper cart-stepper">
                            <button className="stepper-button" onClick={() => updateItem(item.id, Math.max(1, item.quantity - 1))} type="button">-</button>
                            <output>{item.quantity}</output>
                            <button className="stepper-button" onClick={() => updateItem(item.id, Math.min(item.available_quantity, item.quantity + 1))} type="button">+</button>
                          </div>
                        </td>
                        <td>{formatPrice(item.line_total_cents)}</td>
                        <td>
                          <button className="plain-remove" onClick={() => removeItem(item.id)} type="button">
                            x
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <section className="discount-panel">
                <h2>Discount Code</h2>
                <div className="actions">
                  <input aria-label="Discount code" disabled />
                  <button disabled type="button">Apply coupon</button>
                </div>
              </section>
              <a className="button-link secondary-link" href="/" onClick={navigate}>Continue Shopping</a>
            </section>
            <aside className="cart-total-card">
              <h2>Cart Totals</h2>
              <dl>
                <div><dt>Subtotal</dt><dd>{formatPrice(cart.subtotal_cents)}</dd></div>
                <div><dt>Tax</dt><dd>Calculated at checkout</dd></div>
                <div><dt>Total</dt><dd>{formatPrice(cart.subtotal_cents)}</dd></div>
              </dl>
              <a className="checkout-button" href="/checkout" onClick={navigate}>Proceed to Checkout</a>
              <button className="danger" onClick={clearCart} type="button">Clear Cart</button>
            </aside>
          </div>
          <CustomerIdentitySection
            onCustomerChange={(nextCustomer) => setCustomer(nextCustomer)}
            onAddressSelected={(address) => {
              setSelectedAddressId(address.id);
              setShippingDraft({
                ...shippingDraft,
                destination_state: address.state ?? shippingDraft.destination_state,
                destination_county: address.county ?? shippingDraft.destination_county
              });
            }}
          />
          <section className="section-block">
            <h2>Shipping Estimate</h2>
            <div className="form-grid shipping-estimator">
              <label className="checkbox-label span-2">
                <input
                  checked={shippingDraft.local_pickup_eligible}
                  type="checkbox"
                  onChange={(event) =>
                    setShippingDraft({ ...shippingDraft, local_pickup_eligible: event.target.checked })
                  }
                />
                Are you near Draper and want local pickup?
              </label>
              <label>
                Destination State
                <input
                  maxLength={2}
                  value={shippingDraft.destination_state}
                  onChange={(event) =>
                    setShippingDraft({ ...shippingDraft, destination_state: event.target.value.toUpperCase() })
                  }
                />
              </label>
              <label>
                Destination County
                <input
                  value={shippingDraft.destination_county}
                  onChange={(event) => setShippingDraft({ ...shippingDraft, destination_county: event.target.value })}
                />
              </label>
              {shippingMessages.map((message) => (
                <p className="notice span-2" key={message}>
                  {message}
                </p>
              ))}
              {shippingOptions.length === 0 ? <p className="notice span-2">No shipping options are currently available.</p> : null}
              {shippingOptions.length > 0 ? (
                <>
                  <label>
                    Shipping Method
                    <select
                      value={selectedShippingMethod}
                      onChange={(event) => setSelectedShippingMethod(event.target.value)}
                    >
                      {shippingOptions.map((option) => (
                        <option key={option.method} value={option.method}>
                          {option.label} - {formatPrice(option.price_cents)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Pickup / Arrival Date
                    <select value={selectedArrivalDate} onChange={(event) => setSelectedArrivalDate(event.target.value)}>
                      {(shippingOptions.find((option) => option.method === selectedShippingMethod)?.available_arrival_dates ?? []).map(
                        (date) => (
                          <option key={date} value={date}>
                            {date}
                          </option>
                        )
                      )}
                    </select>
                  </label>
                </>
              ) : null}
            </div>
          </section>
          {customer ? (
            <section className="section-block">
              <h2>Add to Existing Order</h2>
              {eligibleAddonOrders.length === 0 ? (
                <p className="notice">No eligible primary orders are available for add-ons.</p>
              ) : (
                <div className="form-grid customer-form">
                  <label>
                    Existing Order
                    <select value={selectedParentOrderId} onChange={(event) => setSelectedParentOrderId(event.target.value)}>
                      {eligibleAddonOrders.map((order) => (
                        <option key={order.id} value={order.id}>
                          {order.order_number} - {order.shipment_method} - {order.arrival_date}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button disabled={!selectedParentOrderId || addonSaving} onClick={createAddonOrder} type="button">
                    {addonSaving ? "Creating" : "Create add-on draft"}
                  </button>
                </div>
              )}
              {addonOrder ? (
                <p className="success">
                  Add-on order {addonOrder.order_number} created for parent {addonOrder.parent_order_number}.
                </p>
              ) : null}
            </section>
          ) : null}
          <section className="cart-summary">
            <h2>Checkout Prep</h2>
            <div className="actions">
              <button disabled={!selectedShippingMethod || !selectedArrivalDate || orderSaving} onClick={createDraftOrder} type="button">
                {orderSaving ? "Creating" : "Create draft order"}
              </button>
            </div>
          </section>
          {draftOrder ? (
            <section className="section-block">
              <h2>Draft Order {draftOrder.order_number}</h2>
              <div className="editor-card">
                <dl>
                  <div>
                    <dt>Status</dt>
                    <dd>{draftOrder.status}</dd>
                  </div>
                  <div>
                    <dt>Shipping</dt>
                    <dd>{draftOrder.shipment_method}</dd>
                  </div>
                  <div>
                    <dt>Pack Date</dt>
                    <dd>{draftOrder.pack_date}</dd>
                  </div>
                  <div>
                    <dt>Arrival / Pickup</dt>
                    <dd>{draftOrder.arrival_date}</dd>
                  </div>
                  <div>
                    <dt>Total</dt>
                    <dd>{formatPrice(draftOrder.total_cents)}</dd>
                  </div>
                </dl>
                {paymentError ? <p className="error">{paymentError}</p> : null}
                <PayNowButton orderId={draftOrder.id} onError={setPaymentError} />
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </main>
  );
}

function CheckoutPage() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [shippingDraft, setShippingDraft] = useState({ local_pickup_eligible: false, destination_state: "UT", destination_county: "" });
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState("");
  const [selectedArrivalDate, setSelectedArrivalDate] = useState("");
  const [draftOrder, setDraftOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/cart")
      .then((response) => response.json() as Promise<CartResponse>)
      .then((data) => {
        setCart(data.cart);
        notifyCartUpdated(data.cart);
      });
  }, []);

  useEffect(() => {
    if (!cart) return;
    const params = new URLSearchParams({
      contains_live_items: cart.items.length > 0 ? "true" : "false",
      contains_non_live_items: "false",
      destination_state: shippingDraft.destination_state,
      destination_county: shippingDraft.destination_county,
      local_pickup_eligible: shippingDraft.local_pickup_eligible ? "true" : "false"
    });
    fetch(`/api/shipping-options?${params.toString()}`)
      .then((response) => response.json() as Promise<ShippingOptionsResponse>)
      .then((data) => {
        setShippingOptions(data.options);
        setSelectedShippingMethod((current) =>
          current && data.options.some((option) => option.method === current) ? current : (data.options[0]?.method ?? "")
        );
      })
      .catch(() => setShippingOptions([]));
  }, [cart?.item_count, shippingDraft]);

  useEffect(() => {
    const option = shippingOptions.find((item) => item.method === selectedShippingMethod);
    setSelectedArrivalDate((current) =>
      current && option?.available_arrival_dates.includes(current) ? current : (option?.available_arrival_dates[0] ?? "")
    );
  }, [selectedShippingMethod, shippingOptions]);

  async function createDraftOrder() {
    setError("");
    setSaving(true);
    const packDate = selectedShippingMethod === "LOCAL_PICKUP" ? selectedArrivalDate : shiftDate(selectedArrivalDate, -1);
    const response = await fetch("/api/orders/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shipment_method: selectedShippingMethod,
        pack_date: packDate,
        arrival_date: selectedArrivalDate,
        shipping_address_id: selectedAddressId,
        shipping_snapshot: selectedAddressId
          ? null
          : {
              shipping_name: null,
              shipping_phone: null,
              shipping_address1: null,
              shipping_address2: null,
              shipping_city: null,
              shipping_state: shippingDraft.destination_state,
              shipping_postal_code: null,
              shipping_country: "US",
              shipping_county: shippingDraft.destination_county
            }
      })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as OrderResponse;
    setDraftOrder(data.order);
  }

  return (
    <main className="page wide">
      <AdminNav />
      <CheckoutSteps current="checkout" />
      <div className="checkout-coupon">Have a coupon? <strong>Enter your code</strong></div>
      <StoreCreditPanel />
      {error ? <p className="error">{error}</p> : null}
      <div className="checkout-template-layout">
        <section className="checkout-billing">
          <h1>Billing details</h1>
          <CustomerIdentitySection
            onCustomerChange={(nextCustomer) => setCustomer(nextCustomer)}
            onAddressSelected={(address) => {
              setSelectedAddressId(address.id);
              setShippingDraft({
                ...shippingDraft,
                destination_state: address.state ?? shippingDraft.destination_state,
                destination_county: address.county ?? shippingDraft.destination_county
              });
            }}
          />
          <section className="section-block">
            <h2>Delivery date</h2>
            <div className="form-grid shipping-estimator">
              <label className="checkbox-label span-2">
                <input
                  checked={shippingDraft.local_pickup_eligible}
                  type="checkbox"
                  onChange={(event) => setShippingDraft({ ...shippingDraft, local_pickup_eligible: event.target.checked })}
                />
                Local pickup eligible
              </label>
              <label>
                Destination State
                <input
                  maxLength={2}
                  value={shippingDraft.destination_state}
                  onChange={(event) => setShippingDraft({ ...shippingDraft, destination_state: event.target.value.toUpperCase() })}
                />
              </label>
              <label>
                Destination County
                <input value={shippingDraft.destination_county} onChange={(event) => setShippingDraft({ ...shippingDraft, destination_county: event.target.value })} />
              </label>
              <label>
                Shipping Method
                <select value={selectedShippingMethod} onChange={(event) => setSelectedShippingMethod(event.target.value)}>
                  {shippingOptions.map((option) => (
                    <option key={option.method} value={option.method}>{option.label} - {formatPrice(option.price_cents)}</option>
                  ))}
                </select>
              </label>
              <label>
                Preferred delivery date
                <select value={selectedArrivalDate} onChange={(event) => setSelectedArrivalDate(event.target.value)}>
                  {(shippingOptions.find((option) => option.method === selectedShippingMethod)?.available_arrival_dates ?? []).map((date) => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        </section>
        <aside className="checkout-order-card">
          <h2>Your Order</h2>
          {cart?.items.map((item) => (
            <article className="checkout-order-line" key={item.id}>
              <div>
                <strong>{item.product_name}</strong>
                <span>{item.variant_name}</span>
              </div>
              <span>{item.quantity}</span>
              <strong>{formatPrice(item.line_total_cents)}</strong>
            </article>
          ))}
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(cart?.subtotal_cents ?? 0)}</dd></div>
            <div><dt>Shipment</dt><dd>{selectedShippingMethod || "Select a method"}</dd></div>
            <div><dt>Total</dt><dd>{formatPrice(cart?.subtotal_cents ?? 0)}</dd></div>
          </dl>
          <div className="free-shipping-bar compact">
            <strong>Your order qualifies for free shipping!</strong>
            <span />
          </div>
          <button disabled={!customer || !cart?.items.length || !selectedShippingMethod || !selectedArrivalDate || saving} onClick={createDraftOrder} type="button">
            {saving ? "Creating" : "Place Order"}
          </button>
          {draftOrder ? (
            <p className="success">
              Draft order {draftOrder.order_number} created. Payment can be completed from the order detail page.
            </p>
          ) : null}
        </aside>
      </div>
    </main>
  );
}

function OrderSummary({ order }: { order: Order }) {
  return (
    <tr>
      <td>
        <a href={`/orders/${order.id}`} onClick={navigate}>
          {order.order_number}
        </a>
      </td>
      <td>{order.order_type}</td>
      <td>{order.parent_order_number ?? ""}</td>
      <td>{order.status}</td>
      <td>{order.shipment_method}</td>
      <td>{order.pack_date}</td>
      <td>{order.arrival_date}</td>
      <td>{formatPrice(order.total_cents)}</td>
    </tr>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/orders/mine")
      .then(async (response) => {
        if (!response.ok) {
          setError(await readApiError(response));
          return null;
        }
        return response.json() as Promise<OrdersResponse>;
      })
      .then((data) => setOrders(data?.orders ?? []));
  }, []);

  return (
    <main className="page wide">
      <AdminNav />
      <header className="page-header">
        <div>
          <p className="eyebrow">LiveFishDirect 2.0</p>
          <h1>Orders</h1>
        </div>
      </header>
      {error ? <p className="error">{error}</p> : null}
      {!error && orders.length === 0 ? <p className="notice">No orders yet.</p> : null}
      {orders.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Type</th>
                <th>Parent</th>
                <th>Status</th>
                <th>Shipping</th>
                <th>Pack Date</th>
                <th>Arrival / Pickup</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>{orders.map((order) => <OrderSummary key={order.id} order={order} />)}</tbody>
          </table>
        </div>
      ) : null}
    </main>
  );
}

function PayNowButton({ orderId, onError }: { orderId: string; onError: (message: string) => void }) {
  const [paying, setPaying] = useState(false);

  async function payNow() {
    setPaying(true);
    onError("");
    const response = await fetch("/api/payments/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_id: orderId })
    });
    setPaying(false);
    if (!response.ok) {
      onError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as CheckoutResponse;
    window.location.href = data.checkout_url;
  }

  return (
    <button disabled={paying} onClick={payNow} type="button">
      {paying ? "Opening Stripe" : "Pay Now"}
    </button>
  );
}

function OrderDetailView({ order, admin }: { order: OrderDetail; admin?: boolean }) {
  const [paymentError, setPaymentError] = useState("");
  const canPay = !admin && (order.status === "DRAFT" || order.status === "PENDING_PAYMENT");

  return (
    <>
      <section className="section-block">
        <div className="editor-card">
          {paymentError ? <p className="error">{paymentError}</p> : null}
          <dl>
            <div>
              <dt>Status</dt>
              <dd>{order.status}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{order.order_type}</dd>
            </div>
            <div>
              <dt>Parent</dt>
              <dd>{order.parent_order_number ?? "None"}</dd>
            </div>
            <div>
              <dt>Customer</dt>
              <dd>{order.customer_email}</dd>
            </div>
            <div>
              <dt>Shipment</dt>
              <dd>{order.shipment_method}</dd>
            </div>
            <div>
              <dt>Pack Date</dt>
              <dd>{order.pack_date}</dd>
            </div>
            <div>
              <dt>Arrival / Pickup</dt>
              <dd>{order.arrival_date}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>{formatPrice(order.total_cents)}</dd>
            </div>
            <div>
              <dt>Payment</dt>
              <dd>{order.payment_status ?? "None"}</dd>
            </div>
            {admin ? (
              <div>
                <dt>Stripe Session</dt>
                <dd>{order.stripe_checkout_session_id ?? ""}</dd>
              </div>
            ) : null}
          </dl>
          {canPay ? <PayNowButton orderId={order.id} onError={setPaymentError} /> : null}
        </div>
      </section>
      {order.addon_orders && order.addon_orders.length > 0 ? (
        <section className="section-block">
          <h2>Add-On Orders</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.addon_orders.map((addon) => (
                  <tr key={addon.id}>
                    <td>{addon.order_number}</td>
                    <td>{addon.status}</td>
                    <td>{formatPrice(addon.total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      <section className="section-block">
        <h2>Items</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Variant</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.product_name}</td>
                  <td>{item.variant_name}</td>
                  <td>{item.sku}</td>
                  <td>{item.quantity}</td>
                  <td>{formatPrice(item.price_cents)}</td>
                  <td>{formatPrice(item.line_total_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {admin && order.reservations && order.reservations.length > 0 ? (
        <section className="section-block">
          <h2>Inventory Reservations</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Variant</th>
                  <th>SKU</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Released At</th>
                </tr>
              </thead>
              <tbody>
                {order.reservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td>{reservation.product_name ?? ""}</td>
                    <td>{reservation.variant_name ?? ""}</td>
                    <td>{reservation.sku ?? ""}</td>
                    <td>{reservation.quantity}</td>
                    <td>{reservation.status}</td>
                    <td>{reservation.released_at ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}

function CustomerOrderDetailPage({ orderId }: { orderId: string }) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`/api/orders/${orderId}`)
      .then(async (response) => {
        if (!response.ok) {
          setError(await readApiError(response));
          return null;
        }
        return response.json() as Promise<OrderResponse>;
      })
      .then((data) => setOrder(data?.order ?? null));
  }, [orderId]);

  return (
    <main className="page wide">
      <AdminNav />
      <header className="page-header">
        <div>
          <p className="eyebrow">Order</p>
          <h1>{order?.order_number ?? "Order"}</h1>
        </div>
      </header>
      {error ? <p className="error">{error}</p> : null}
      {order ? <OrderDetailView order={order} /> : null}
    </main>
  );
}

function InventoryRow({ row, onSaved }: { row: AdminInventoryRow; onSaved: () => void }) {
  const [draft, setDraft] = useState({
    tank_number: row.tank_number ?? "",
    quantity: row.quantity,
    reserved_quantity: row.reserved_quantity,
    price: dollars(row.price_cents)
  });
  const [saving, setSaving] = useState(false);

  const available = useMemo(() => draft.quantity - draft.reserved_quantity, [draft.quantity, draft.reserved_quantity]);

  async function save() {
    setSaving(true);
    await fetch(`/api/admin/inventory/${row.inventory_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tank_number: draft.tank_number,
        quantity: draft.quantity,
        reserved_quantity: draft.reserved_quantity,
        price_cents: centsFromInput(draft.price)
      })
    });
    setSaving(false);
    onSaved();
  }

  return (
    <tr className={`inventory-${getStockState(available)}`}>
      <td>{row.product_name}</td>
      <td>{row.variant_name}</td>
      <td>{row.sku}</td>
      <td>
        <input value={draft.tank_number} onChange={(event) => setDraft({ ...draft, tank_number: event.target.value })} />
      </td>
      <td>
        <input min="0" type="number" value={draft.quantity} onChange={(event) => setDraft({ ...draft, quantity: event.target.valueAsNumber })} />
      </td>
      <td>
        <input min="0" type="number" value={draft.reserved_quantity} onChange={(event) => setDraft({ ...draft, reserved_quantity: event.target.valueAsNumber })} />
      </td>
      <td>
        <span className={`stock-pill stock-${getStockState(available)}`}>{available}</span>
      </td>
      <td>
        <input min="0" step="0.01" type="number" value={draft.price} onChange={(event) => setDraft({ ...draft, price: event.target.value })} />
      </td>
      <td>{row.status}</td>
      <td>
        <button disabled={saving} onClick={save} type="button">
          {saving ? "Saving" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function AdminInventoryPage({ user }: { user: AdminUser }) {
  const [rows, setRows] = useState<AdminInventoryRow[]>([]);
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState<"all" | "in" | "low" | "out">("all");
  const loadInventory = () => {
    fetch("/api/admin/inventory")
      .then((response) => response.json() as Promise<InventoryResponse>)
      .then((data) => setRows(data.inventory));
  };

  useEffect(loadInventory, []);

  const filteredRows = rows.filter((row) => {
    const searchText = [row.product_name, row.variant_name, row.sku, row.tank_number ?? ""].join(" ").toLowerCase();
    const matchesSearch = searchText.includes(search.toLowerCase());
    const stockState = getStockState(row.available_quantity);
    const matchesStock = stockFilter === "all" || stockFilter === stockState;
    return matchesSearch && matchesStock;
  });

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Inventory</h1>
        </div>
        <AdminNav user={user} />
      </header>

      <section className="control-bar" aria-label="Inventory filters">
        <label>
          Search
          <input
            placeholder="Product, variant, SKU, tank"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          Stock
          <select value={stockFilter} onChange={(event) => setStockFilter(event.target.value as typeof stockFilter)}>
            <option value="all">All</option>
            <option value="in">In stock</option>
            <option value="low">Low stock</option>
            <option value="out">Out of stock</option>
          </select>
        </label>
      </section>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Variant</th>
              <th>SKU</th>
              <th>Tank Number</th>
              <th>Quantity</th>
              <th>Reserved</th>
              <th>Available</th>
              <th>Price</th>
              <th>Status</th>
              <th>Save</th>
            </tr>
          </thead>
          <tbody>{filteredRows.map((row) => <InventoryRow key={row.inventory_id} row={row} onSaved={loadInventory} />)}</tbody>
        </table>
      </div>
    </main>
  );
}

function ProductForm({
  categories,
  initial,
  onSubmit,
  saving,
  autoGenerateSlug = false
}: {
  categories: Category[];
  initial: {
    name: string;
    slug: string;
    description: string;
    category_id: string;
    status: ProductStatus;
  };
  onSubmit: (value: typeof initial) => void;
  saving: boolean;
  autoGenerateSlug?: boolean;
}) {
  const [draft, setDraft] = useState(initial);
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    setDraft(initial);
    setSlugEdited(false);
  }, [initial]);

  return (
    <form className="form-grid" onSubmit={(event) => {
      event.preventDefault();
      onSubmit(draft);
    }}>
      <label>
        Name
        <input
          required
          value={draft.name}
          onChange={(event) => {
            const name = event.target.value;
            setDraft({
              ...draft,
              name,
              slug: autoGenerateSlug && !slugEdited ? makeSlug(name) : draft.slug
            });
          }}
        />
      </label>
      <label>
        Slug
        <input
          required
          value={draft.slug}
          onChange={(event) => {
            setSlugEdited(true);
            setDraft({ ...draft, slug: makeSlug(event.target.value) });
          }}
        />
      </label>
      <label>
        Category
        <select value={draft.category_id} onChange={(event) => setDraft({ ...draft, category_id: event.target.value })}>
          <option value="">Uncategorized</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </label>
      {categories.length === 0 ? (
        <p className="notice span-2">
          No categories exist yet. Create one in Admin Categories when you are ready to organize products.
        </p>
      ) : null}
      <label>
        Status
        <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as ProductStatus })}>
          {productStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <label className="span-2">
        Description
        <textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
      </label>
      <button disabled={saving} type="submit">
        {saving ? "Saving" : "Save Product"}
      </button>
    </form>
  );
}

function AdminProductsPage({ user }: { user: AdminUser }) {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [inventory, setInventory] = useState<AdminInventoryRow[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | ProductStatus>("ALL");

  useEffect(() => {
    Promise.all([
      fetch("/api/admin/products").then((response) => response.json() as Promise<AdminProductsResponse>),
      fetch("/api/admin/inventory").then((response) => response.json() as Promise<InventoryResponse>)
    ]).then(([productsData, inventoryData]) => {
      setProducts(productsData.products);
      setInventory(inventoryData.inventory);
    });
  }, []);

  const filteredProducts = products.filter((product) => {
    const skus = inventory
      .filter((row) => row.product_id === product.id)
      .map((row) => row.sku)
      .join(" ");
    const searchText = [product.name, product.slug, product.category?.name ?? "", skus].join(" ").toLowerCase();
    const matchesSearch = searchText.includes(search.toLowerCase());
    const matchesStatus = statusFilter === "ALL" || product.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Products</h1>
        </div>
        <div className="actions">
          <a className="button-link" href="/admin/products/new" onClick={navigate}>
            New Product
          </a>
          <AdminNav user={user} />
        </div>
      </header>

      <section className="control-bar" aria-label="Product filters">
        <label>
          Search
          <input
            placeholder="Name, slug, SKU, category"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
            <option value="ALL">All</option>
            {productStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Category</th>
              <th>Status</th>
              <th>Variants</th>
              <th>Edit</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.slug}</td>
                <td>{product.category?.name ?? "Uncategorized"}</td>
                <td>{product.status}</td>
                <td>{product.variant_count}</td>
                <td>
                  <a href={`/admin/products/${product.id}`} onClick={navigate}>
                    Edit
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function NewProductPage({ user }: { user: AdminUser }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/admin/categories")
      .then((response) => response.json() as Promise<CategoriesResponse>)
      .then((data) => setCategories(data.categories));
  }, []);

  async function createProduct(value: { name: string; slug: string; description: string; category_id: string; status: ProductStatus }) {
    setSaving(true);
    setError("");
    const response = await fetch("/api/admin/products", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...value, category_id: value.category_id || null })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as { id: string };
    goTo(`/admin/products/${data.id}`);
  }

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>New Product</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      <ProductForm
        categories={categories}
        initial={{ name: "", slug: "", description: "", category_id: "", status: "DRAFT" }}
        onSubmit={createProduct}
        saving={saving}
        autoGenerateSlug
      />
    </main>
  );
}

function CategoryEditor({ category, onReload }: { category: CategoryWithCount; onReload: () => void }) {
  const [draft, setDraft] = useState({ name: category.name, slug: category.slug });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft({ name: category.name, slug: category.slug }), [category]);

  async function save() {
    setSaving(true);
    setError("");
    const response = await fetch(`/api/admin/categories/${category.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    onReload();
  }

  async function remove() {
    setError("");
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    const response = await fetch(`/api/admin/categories/${category.id}`, { method: "DELETE" });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    onReload();
  }

  return (
    <div className="editor-card">
      {error ? <p className="error">{error}</p> : null}
      <div className="category-row">
        <label>
          Name
          <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          Slug
          <input value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: event.target.value })} />
        </label>
        <div>
          <dt>Products</dt>
          <dd>{category.product_count}</dd>
        </div>
        <div className="actions">
          <button disabled={saving} onClick={save} type="button">
            {saving ? "Saving" : "Save"}
          </button>
          <button className="danger" onClick={remove} type="button">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminCategoriesPage({ user }: { user: AdminUser }) {
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [draft, setDraft] = useState({ name: "", slug: "" });
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadCategories = () => {
    fetch("/api/admin/categories")
      .then((response) => response.json() as Promise<CategoriesResponse>)
      .then((data) => setCategories(data.categories));
  };

  useEffect(loadCategories, []);

  async function createCategory(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/admin/categories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setDraft({ name: "", slug: "" });
    setSlugEdited(false);
    loadCategories();
  }

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Categories</h1>
        </div>
        <AdminNav user={user} />
      </header>

      <section className="section-block">
        <h2>Create Category</h2>
        {error ? <p className="error">{error}</p> : null}
        <form className="form-grid" onSubmit={createCategory}>
          <label>
            Name
            <input
              required
              value={draft.name}
              onChange={(event) => {
                const name = event.target.value;
                setDraft({ ...draft, name, slug: slugEdited ? draft.slug : makeSlug(name) });
              }}
            />
          </label>
          <label>
            Slug
            <input
              required
              value={draft.slug}
              onChange={(event) => {
                setSlugEdited(true);
                setDraft({ ...draft, slug: makeSlug(event.target.value) });
              }}
            />
          </label>
          <button disabled={saving} type="submit">
            {saving ? "Creating" : "Create Category"}
          </button>
        </form>
      </section>

      <section className="section-block">
        <h2>Manage Categories</h2>
        {categories.length === 0 ? <p className="notice">No categories exist yet.</p> : null}
        <div className="editor-list">
          {categories.map((category) => (
            <CategoryEditor category={category} key={category.id} onReload={loadCategories} />
          ))}
        </div>
      </section>
    </main>
  );
}

function getDeliveryStatus(deliveryDate: DeliveryDate) {
  if (deliveryDate.enabled !== 1) return "Disabled";
  if (deliveryDate.capacity <= 0 || deliveryDate.used_slots >= deliveryDate.capacity) return "Full";
  return "Available";
}

function DeliveryDateEditor({ deliveryDate, onReload }: { deliveryDate: DeliveryDate; onReload: () => void }) {
  const [draft, setDraft] = useState({
    date: deliveryDate.date,
    enabled: deliveryDate.enabled === 1,
    capacity: deliveryDate.capacity,
    used_slots: deliveryDate.used_slots,
    note: deliveryDate.note ?? ""
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const status = getDeliveryStatus(deliveryDate);

  useEffect(() => {
    setDraft({
      date: deliveryDate.date,
      enabled: deliveryDate.enabled === 1,
      capacity: deliveryDate.capacity,
      used_slots: deliveryDate.used_slots,
      note: deliveryDate.note ?? ""
    });
  }, [deliveryDate]);

  async function save() {
    setSaving(true);
    setError("");
    const response = await fetch(`/api/admin/delivery-dates/${deliveryDate.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date: draft.date,
        enabled: draft.enabled ? 1 : 0,
        capacity: draft.capacity,
        used_slots: draft.used_slots,
        note: draft.note || null
      })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    onReload();
  }

  async function remove() {
    setError("");
    if (!window.confirm(`Delete delivery date "${deliveryDate.date}"?`)) return;
    const response = await fetch(`/api/admin/delivery-dates/${deliveryDate.id}`, { method: "DELETE" });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    onReload();
  }

  return (
    <div className={`editor-card delivery-card delivery-${status.toLowerCase()}`}>
      {error ? <p className="error">{error}</p> : null}
      <div className="delivery-row">
        <label>
          Date
          <input required type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
        </label>
        <label className="checkbox-label">
          <input
            checked={draft.enabled}
            type="checkbox"
            onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
          />
          Enabled
        </label>
        <label>
          Capacity
          <input
            min="0"
            type="number"
            value={draft.capacity}
            onChange={(event) => setDraft({ ...draft, capacity: Math.max(0, event.target.valueAsNumber || 0) })}
          />
        </label>
        <label>
          Used
          <input
            min="0"
            type="number"
            value={draft.used_slots}
            onChange={(event) => setDraft({ ...draft, used_slots: Math.max(0, event.target.valueAsNumber || 0) })}
          />
        </label>
        <label>
          Note
          <input value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
        </label>
        <span className={`status-pill delivery-status-${status.toLowerCase()}`}>{status}</span>
        <div className="actions">
          <button disabled={saving} onClick={save} type="button">
            {saving ? "Saving" : "Save"}
          </button>
          <button className="danger" onClick={remove} type="button">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminDeliveryDatesPage({ user }: { user: AdminUser }) {
  const [deliveryDates, setDeliveryDates] = useState<DeliveryDate[]>([]);
  const [draft, setDraft] = useState({ date: "", enabled: true, capacity: 0, used_slots: 0, note: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDeliveryDates = () => {
    fetch("/api/admin/delivery-dates")
      .then((response) => response.json() as Promise<DeliveryDatesResponse>)
      .then((data) => setDeliveryDates(data.delivery_dates));
  };

  useEffect(loadDeliveryDates, []);

  async function createDeliveryDate(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch("/api/admin/delivery-dates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date: draft.date,
        enabled: draft.enabled ? 1 : 0,
        capacity: draft.capacity,
        used_slots: draft.used_slots,
        note: draft.note || null
      })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setDraft({ date: "", enabled: true, capacity: 0, used_slots: 0, note: "" });
    loadDeliveryDates();
  }

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Delivery Dates</h1>
        </div>
        <AdminNav user={user} />
      </header>

      <section className="section-block">
        <h2>Create Delivery Date</h2>
        {error ? <p className="error">{error}</p> : null}
        <form className="form-grid delivery-form" onSubmit={createDeliveryDate}>
          <label>
            Date
            <input required type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
          </label>
          <label className="checkbox-label">
            <input
              checked={draft.enabled}
              type="checkbox"
              onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
            />
            Enabled
          </label>
          <label>
            Capacity
            <input
              min="0"
              type="number"
              value={draft.capacity}
              onChange={(event) => setDraft({ ...draft, capacity: Math.max(0, event.target.valueAsNumber || 0) })}
            />
          </label>
          <label>
            Used Slots
            <input
              min="0"
              type="number"
              value={draft.used_slots}
              onChange={(event) => setDraft({ ...draft, used_slots: Math.max(0, event.target.valueAsNumber || 0) })}
            />
          </label>
          <label className="span-2">
            Note
            <input value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
          </label>
          <button disabled={saving} type="submit">
            {saving ? "Creating" : "Create Delivery Date"}
          </button>
        </form>
      </section>

      <section className="section-block">
        <h2>Manage Delivery Dates</h2>
        {deliveryDates.length === 0 ? <p className="notice">No delivery dates exist yet.</p> : null}
        <div className="editor-list">
          {deliveryDates.map((deliveryDate) => (
            <DeliveryDateEditor deliveryDate={deliveryDate} key={deliveryDate.id} onReload={loadDeliveryDates} />
          ))}
        </div>
      </section>
    </main>
  );
}

function AdminShippingPage({ user }: { user: AdminUser }) {
  const [settings, setSettings] = useState({
    local_pickup_cents: "0.00",
    utah_ground_cents: "10.00",
    utah_priority_cents: "25.00",
    nationwide_live_cents: "55.00"
  });
  const [capacities, setCapacities] = useState<PackDayCapacity[]>(
    dayNames.map((_, index) => ({ day_of_week: index, capacity: index >= 1 && index <= 3 ? 40 : 0, updated_at: 0 }))
  );
  const [shipmentGroups, setShipmentGroups] = useState<ShipmentGroup[]>([]);
  const [priceMessage, setPriceMessage] = useState("");
  const [capacityMessage, setCapacityMessage] = useState("");
  const [error, setError] = useState("");
  const [savingPrices, setSavingPrices] = useState(false);
  const [savingCapacity, setSavingCapacity] = useState(false);

  const loadShipping = () => {
    Promise.all([
      fetch("/api/admin/shipping-settings").then((response) => response.json() as Promise<ShippingSettingsResponse>),
      fetch("/api/admin/pack-day-capacity").then((response) => response.json() as Promise<PackDayCapacityResponse>),
      fetch("/api/admin/shipment-groups").then((response) => response.json() as Promise<ShipmentGroupsResponse>)
    ])
      .then(([settingsData, capacityData, groupsData]) => {
        setSettings({
          local_pickup_cents: dollars(settingsData.settings.local_pickup_cents),
          utah_ground_cents: dollars(settingsData.settings.utah_ground_cents),
          utah_priority_cents: dollars(settingsData.settings.utah_priority_cents),
          nationwide_live_cents: dollars(settingsData.settings.nationwide_live_cents)
        });
        setCapacities(capacityData.capacities);
        setShipmentGroups(groupsData.shipment_groups);
      })
      .catch(() => setError("Shipping settings could not be loaded."));
  };

  useEffect(loadShipping, []);

  async function savePrices(event: FormEvent) {
    event.preventDefault();
    setSavingPrices(true);
    setError("");
    setPriceMessage("");
    const response = await fetch("/api/admin/shipping-settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        local_pickup_cents: centsFromInput(settings.local_pickup_cents),
        utah_ground_cents: centsFromInput(settings.utah_ground_cents),
        utah_priority_cents: centsFromInput(settings.utah_priority_cents),
        nationwide_live_cents: centsFromInput(settings.nationwide_live_cents)
      })
    });
    setSavingPrices(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setPriceMessage("Shipping prices saved.");
    loadShipping();
  }

  async function saveCapacity(event: FormEvent) {
    event.preventDefault();
    setSavingCapacity(true);
    setError("");
    setCapacityMessage("");
    const response = await fetch("/api/admin/pack-day-capacity", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        capacities: capacities.map((row) => ({
          day_of_week: row.day_of_week,
          capacity: row.capacity
        }))
      })
    });
    setSavingCapacity(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setCapacityMessage("Pack day capacity saved.");
    loadShipping();
  }

  function updateCapacity(dayOfWeek: number, capacity: number) {
    setCapacities(
      capacities.map((row) => (row.day_of_week === dayOfWeek ? { ...row, capacity: Math.max(0, capacity) } : row))
    );
  }

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Shipping</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}

      <section className="section-block">
        <h2>Flat Prices</h2>
        {priceMessage ? <p className="success">{priceMessage}</p> : null}
        <form className="form-grid" onSubmit={savePrices}>
          <label>
            Local Pickup
            <input
              value={settings.local_pickup_cents}
              onChange={(event) => setSettings({ ...settings, local_pickup_cents: event.target.value })}
            />
          </label>
          <label>
            Utah Ground
            <input
              value={settings.utah_ground_cents}
              onChange={(event) => setSettings({ ...settings, utah_ground_cents: event.target.value })}
            />
          </label>
          <label>
            Utah Priority
            <input
              value={settings.utah_priority_cents}
              onChange={(event) => setSettings({ ...settings, utah_priority_cents: event.target.value })}
            />
          </label>
          <label>
            Nationwide Live Fish
            <input
              value={settings.nationwide_live_cents}
              onChange={(event) => setSettings({ ...settings, nationwide_live_cents: event.target.value })}
            />
          </label>
          <button disabled={savingPrices} type="submit">
            {savingPrices ? "Saving" : "Save Prices"}
          </button>
        </form>
      </section>

      <section className="section-block">
        <h2>Pack Day Capacity</h2>
        <p className="notice">Capacity 0 disables that pack day. Default pack days are Monday, Tuesday, and Wednesday.</p>
        {capacityMessage ? <p className="success">{capacityMessage}</p> : null}
        <form className="form-grid capacity-grid" onSubmit={saveCapacity}>
          {capacities.map((row) => (
            <label key={row.day_of_week}>
              {dayNames[row.day_of_week]}
              <input
                min="0"
                type="number"
                value={row.capacity}
                onChange={(event) => updateCapacity(row.day_of_week, event.target.valueAsNumber || 0)}
              />
            </label>
          ))}
          <button disabled={savingCapacity} type="submit">
            {savingCapacity ? "Saving" : "Save Capacity"}
          </button>
        </form>
      </section>

      <section className="section-block">
        <h2>Shipment Groups</h2>
        {shipmentGroups.length === 0 ? <p className="notice">No shipment groups exist yet.</p> : null}
        {shipmentGroups.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Pack Date</th>
                  <th>Arrival Date</th>
                  <th>Status</th>
                  <th>Primary</th>
                </tr>
              </thead>
              <tbody>
                {shipmentGroups.map((group) => (
                  <tr key={group.id}>
                    <td>{group.shipment_method}</td>
                    <td>{group.pack_date}</td>
                    <td>{group.arrival_date}</td>
                    <td>{group.status}</td>
                    <td>{group.is_primary === 1 ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </main>
  );
}

function AdminOrdersPage({ user }: { user: AdminUser }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/admin/orders")
      .then(async (response) => {
        if (!response.ok) {
          setError(await readApiError(response));
          return null;
        }
        return response.json() as Promise<OrdersResponse>;
      })
      .then((data) => setOrders(data?.orders ?? []));
  }, []);

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Orders</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      {orders.length === 0 ? <p className="notice">No orders exist yet.</p> : null}
      {orders.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Type</th>
                <th>Parent</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Shipment</th>
                <th>Pack Date</th>
                <th>Total</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.order_number}</td>
                  <td>{order.order_type}</td>
                  <td>{order.parent_order_number ?? ""}</td>
                  <td>{order.customer_email}</td>
                  <td>{order.status}</td>
                  <td>{order.payment_status ?? ""}</td>
                  <td>{order.shipment_method}</td>
                  <td>{order.pack_date}</td>
                  <td>{formatPrice(order.total_cents)}</td>
                  <td>
                    <a href={`/admin/orders/${order.id}`} onClick={navigate}>
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </main>
  );
}

function AdminOrderDetailPage({ orderId, user }: { orderId: string; user: AdminUser }) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [status, setStatus] = useState<OrderStatus>("DRAFT");
  const [error, setError] = useState("");

  const loadOrder = () => {
    fetch(`/api/admin/orders/${orderId}`)
      .then(async (response) => {
        if (!response.ok) {
          setError(await readApiError(response));
          return null;
        }
        return response.json() as Promise<OrderResponse>;
      })
      .then((data) => {
        if (data?.order) {
          setOrder(data.order);
          setStatus(data.order.status);
        }
      });
  };

  useEffect(loadOrder, [orderId]);

  async function saveStatus() {
    setError("");
    const response = await fetch(`/api/admin/orders/${orderId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as OrderResponse;
    setOrder(data.order);
  }

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin Orders</p>
          <h1>{order?.order_number ?? "Order"}</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      {order ? (
        <>
          <section className="section-block">
            <h2>Status</h2>
            <div className="form-grid customer-form">
              <label>
                Order Status
                <select value={status} onChange={(event) => setStatus(event.target.value as OrderStatus)}>
                  {orderStatuses.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
              <button onClick={saveStatus} type="button">
                Save Status
              </button>
            </div>
          </section>
          <OrderDetailView admin order={order} />
        </>
      ) : null}
    </main>
  );
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function AdminPackingPage({ user }: { user: AdminUser }) {
  const [packDate, setPackDate] = useState(todayInputValue());
  const [dashboard, setDashboard] = useState<PackingDashboard | null>(null);
  const [error, setError] = useState("");

  const loadPacking = () => {
    setError("");
    fetch(`/api/admin/packing?pack_date=${packDate}`)
      .then(async (response) => {
        if (!response.ok) {
          setError(await readApiError(response));
          return null;
        }
        return response.json() as Promise<PackingResponse>;
      })
      .then((data) => setDashboard(data?.dashboard ?? null));
  };

  useEffect(loadPacking, [packDate]);

  const downloadLabelsCsv = () => {
    window.location.href = `/api/admin/packing/labels.csv?pack_date=${encodeURIComponent(packDate)}`;
  };

  const renderOrders = (title: string, orders: PackingDashboard["sections"]["local_pickup"]) => (
    <section className="section-block">
      <h2>{title}</h2>
      {orders.length === 0 ? <p className="notice">No orders in this section.</p> : null}
      {orders.map((order) => (
        <details className="editor-card" key={order.order_number}>
          <summary>
            {order.order_number} - {order.customer_name} - {order.fish_quantity} fish - {formatPrice(order.grand_total_cents)}
          </summary>
          <dl>
            <div><dt>Email</dt><dd>{order.customer_email}</dd></div>
            <div><dt>Phone</dt><dd>{order.customer_phone ?? ""}</dd></div>
            <div><dt>Address</dt><dd>{[order.address, order.city, order.state, order.postal_code].filter(Boolean).join(", ")}</dd></div>
            <div><dt>Shipping</dt><dd>{order.shipment_method}</dd></div>
            <div><dt>Arrival</dt><dd>{order.arrival_date}</dd></div>
            <div><dt>Status</dt><dd>{order.status}</dd></div>
            <div><dt>Add-ons</dt><dd>{order.addon_order_numbers.join(", ")}</dd></div>
            <div><dt>Fish Inches</dt><dd>{order.fish_inches.toFixed(2)}</dd></div>
          </dl>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Product</th><th>Variant</th><th>SKU</th><th>Tank</th><th>Qty</th><th>Length</th><th>Total</th></tr></thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={`${order.order_number}-${item.sku}-${item.tank_number}`}>
                    <td>{item.product}</td>
                    <td>{item.variant}</td>
                    <td>{item.sku}</td>
                    <td>{item.tank_number ?? ""}</td>
                    <td>{item.quantity}</td>
                    <td>{item.fish_length_used}</td>
                    <td>{formatPrice(item.line_total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </section>
  );

  return (
    <main className="page wide">
      <header className="page-header">
        <div><p className="eyebrow">Admin</p><h1>Packing</h1></div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      <section className="section-block">
        <div className="form-grid customer-form">
          <label>Pack Date<input type="date" value={packDate} onChange={(event) => setPackDate(event.target.value)} /></label>
          <div className="actions">
            <button disabled type="button">Print Customer Slips (coming soon)</button>
            <button type="button" onClick={downloadLabelsCsv}>Export Bag Labels CSV</button>
            <button disabled type="button">Send Packing Notices (coming soon)</button>
          </div>
        </div>
      </section>
      {dashboard ? (
        <>
          <section className="section-block">
            <h2>Summary</h2>
            <div className="product-list">
              <div className="editor-card"><dt>Primary Orders</dt><dd>{dashboard.primary_order_count}</dd></div>
              <div className="editor-card"><dt>Local Pickup</dt><dd>{dashboard.local_pickup_count}</dd></div>
              <div className="editor-card"><dt>Ground</dt><dd>{dashboard.ground_count}</dd></div>
              <div className="editor-card"><dt>Overnight</dt><dd>{dashboard.overnight_count}</dd></div>
              <div className="editor-card"><dt>Fish</dt><dd>{dashboard.total_fish_quantity}</dd></div>
              <div className="editor-card"><dt>Fish Inches</dt><dd>{dashboard.total_fish_inches.toFixed(2)}</dd></div>
              <div className="editor-card"><dt>Grand Total</dt><dd>{formatPrice(dashboard.grand_total_cents)}</dd></div>
            </div>
          </section>
          {renderOrders("LOCAL PICKUP", dashboard.sections.local_pickup)}
          {renderOrders("GROUND", dashboard.sections.ground)}
          {renderOrders("OVERNIGHT", dashboard.sections.overnight)}
          <section className="section-block">
            <h2>By Variant</h2>
            <div className="table-wrap"><table><thead><tr><th>Product</th><th>Variant</th><th>SKU</th><th>Tank</th><th>Qty</th><th>Inches</th></tr></thead><tbody>{dashboard.by_variant.map((row) => <tr key={`${row.sku}-${row.tank_number}`}><td>{row.product}</td><td>{row.variant}</td><td>{row.sku}</td><td>{row.tank_number ?? ""}</td><td>{row.total_quantity}</td><td>{row.total_inches.toFixed(2)}</td></tr>)}</tbody></table></div>
          </section>
          <section className="section-block">
            <h2>By Tank</h2>
            <div className="table-wrap"><table><thead><tr><th>Tank</th><th>Qty</th><th>Inches</th><th>Items</th></tr></thead><tbody>{dashboard.by_tank.map((row) => <tr key={row.tank_number ?? "unassigned"}><td>{row.tank_number ?? ""}</td><td>{row.total_quantity}</td><td>{row.total_inches.toFixed(2)}</td><td>{row.items.map((item) => `${item.sku} (${item.total_quantity})`).join(", ")}</td></tr>)}</tbody></table></div>
          </section>
        </>
      ) : null}
    </main>
  );
}

function AdminEmailTemplatesPage({ user }: { user: AdminUser }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState({ subject: "", body: "", enabled: 1 });
  const [testRecipient, setTestRecipient] = useState("josh@livefishdirect.com");
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const variables = ["customer_name", "order_number", "arrival_date", "pack_date", "tracking_number", "pickup_address", "order_total", "shipping_method", "company_name"];

  const selected = templates.find((template) => template.id === selectedId);
  const loadTemplates = () => {
    fetch("/api/admin/email-templates")
      .then((response) => response.json() as Promise<EmailTemplatesResponse>)
      .then((data) => {
        setTemplates(data.templates);
        const first = data.templates[0];
        if (first && !selectedId) {
          setSelectedId(first.id);
          setDraft({ subject: first.subject, body: first.body, enabled: first.enabled });
        }
      });
  };

  useEffect(loadTemplates, []);
  useEffect(() => {
    if (selected) setDraft({ subject: selected.subject, body: selected.body, enabled: selected.enabled });
  }, [selectedId]);

  async function saveTemplate(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSuccess("");
    const response = await fetch(`/api/admin/email-templates/${selectedId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    loadTemplates();
    setSuccess("Template saved.");
  }

  async function previewTemplate() {
    setError("");
    setSuccess("");
    const response = await fetch("/api/admin/email-templates/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template_id: selectedId })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as EmailPreviewResponse;
    setPreview(`Subject: ${data.preview.subject}\n\n${data.preview.body}`);
  }

  async function sendTestEmail() {
    setError("");
    setSuccess("");
    const response = await fetch(`/api/admin/email-templates/${selectedId}/send-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recipient_email: testRecipient || "josh@livefishdirect.com" })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setSuccess("Test email queued.");
  }

  return (
    <main className="page wide">
      <header className="page-header">
        <div><p className="eyebrow">Admin</p><h1>Email Templates</h1></div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      {success ? <p className="success">{success}</p> : null}
      <section className="section-block">
        <div className="form-grid">
          <label>Template<select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>{templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}</select></label>
          <label className="checkbox-label"><input checked={draft.enabled === 1} type="checkbox" onChange={(event) => setDraft({ ...draft, enabled: event.target.checked ? 1 : 0 })} />Enabled</label>
        </div>
        <form className="form-grid" onSubmit={saveTemplate}>
          <label className="span-2">Subject<input value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} /></label>
          <label className="span-2">Body<textarea value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} /></label>
          <button type="submit">Save Template</button>
          <button className="secondary" onClick={previewTemplate} type="button">Preview Template</button>
        </form>
        <div className="form-grid">
          <label>
            Test Recipient
            <input value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} />
          </label>
          <button className="secondary" onClick={sendTestEmail} type="button">
            Send Test
          </button>
        </div>
      </section>
      <section className="section-block">
        <h2>Available Variables</h2>
        <p className="notice">{variables.map((variable) => `{{${variable}}}`).join(" ")}</p>
        {preview ? <pre className="editor-card">{preview}</pre> : null}
      </section>
    </main>
  );
}

function AdminSettingsPage({ user }: { user: AdminUser }) {
  const [draft, setDraft] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (draft.new_password !== draft.confirm_password) {
      setError("New password and confirmation must match.");
      return;
    }

    setSaving(true);
    const response = await fetch("/api/auth/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: draft.current_password,
        new_password: draft.new_password
      })
    });
    setSaving(false);

    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }

    const data = (await response.json()) as ChangePasswordResponse;
    if (data.ok) {
      setDraft({ current_password: "", new_password: "", confirm_password: "" });
      setSuccess("Password updated successfully.");
    }
  }

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Settings</h1>
        </div>
        <AdminNav user={user} />
      </header>

      <section className="section-block">
        <h2>Account</h2>
        <p className="notice">Signed in as {user.email}</p>
        <a className="button-link" href="/admin/pages" onClick={navigate}>
          Admin Pages
        </a>
      </section>

      <section className="section-block">
        <h2>Change Password</h2>
        {error ? <p className="error">{error}</p> : null}
        {success ? <p className="success">{success}</p> : null}
        <form className="form-grid login-form" onSubmit={changePassword}>
          <label>
            Current Password
            <input
              required
              type="password"
              value={draft.current_password}
              onChange={(event) => setDraft({ ...draft, current_password: event.target.value })}
            />
          </label>
          <label>
            New Password
            <input
              minLength={12}
              required
              type="password"
              value={draft.new_password}
              onChange={(event) => setDraft({ ...draft, new_password: event.target.value })}
            />
          </label>
          <label>
            Confirm New Password
            <input
              minLength={12}
              required
              type="password"
              value={draft.confirm_password}
              onChange={(event) => setDraft({ ...draft, confirm_password: event.target.value })}
            />
          </label>
          <button disabled={saving} type="submit">
            {saving ? "Updating" : "Update Password"}
          </button>
        </form>
      </section>
    </main>
  );
}

function VariantEditor({ variant, onReload }: { variant: ProductVariant; onReload: () => void }) {
  const [draft, setDraft] = useState({
    name: variant.name,
    sku: variant.sku,
    price: dollars(variant.price_cents),
    fish_size: variant.fish_size ?? "",
    length_min: variant.length_min?.toString() ?? "",
    length_max: variant.length_max?.toString() ?? "",
    sex: variant.sex ?? "",
    status: variant.status
  });
  const [error, setError] = useState("");

  async function save() {
    setError("");
    const response = await fetch(`/api/admin/variants/${variant.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: draft.name,
        sku: draft.sku,
        price_cents: centsFromInput(draft.price),
        fish_size: draft.fish_size || null,
        length_min: draft.length_min ? Number(draft.length_min) : null,
        length_max: draft.length_max ? Number(draft.length_max) : null,
        sex: draft.sex || null,
        status: draft.status
      })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    onReload();
  }

  async function remove() {
    if (!window.confirm(`Delete ${variant.name}?`)) return;
    await fetch(`/api/admin/variants/${variant.id}`, { method: "DELETE" });
    onReload();
  }

  return (
    <div className="editor-card">
      {error ? <p className="error">{error}</p> : null}
      <div className="form-grid compact">
        <label>
          Name
          <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          SKU
          <input value={draft.sku} onChange={(event) => setDraft({ ...draft, sku: event.target.value })} />
        </label>
        <label>
          Price
          <input min="0" step="0.01" type="number" value={draft.price} onChange={(event) => setDraft({ ...draft, price: event.target.value })} />
        </label>
        <label>
          Size
          <input value={draft.fish_size} onChange={(event) => setDraft({ ...draft, fish_size: event.target.value })} />
        </label>
        <label>
          Min Length
          <input min="0" step="0.01" type="number" value={draft.length_min} onChange={(event) => setDraft({ ...draft, length_min: event.target.value })} />
        </label>
        <label>
          Max Length
          <input min="0" step="0.01" type="number" value={draft.length_max} onChange={(event) => setDraft({ ...draft, length_max: event.target.value })} />
        </label>
        <label>
          Sex
          <input value={draft.sex} onChange={(event) => setDraft({ ...draft, sex: event.target.value })} />
        </label>
        <label>
          Status
          <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as VariantStatus })}>
            {variantStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted">
        Inventory: tank {variant.tank_number ?? "N/A"}, quantity {variant.quantity}, reserved {variant.reserved_quantity}, available {variant.available_quantity}
      </p>
      <div className="actions">
        <button onClick={save} type="button">
          Save Variant
        </button>
        <button className="danger" onClick={remove} type="button">
          Delete Variant
        </button>
      </div>
    </div>
  );
}

function NewVariantForm({ productId, onCreated }: { productId: string; onCreated: () => void }) {
  const [draft, setDraft] = useState({
    name: "",
    sku: "",
    price: "",
    fish_size: "",
    length_min: "",
    length_max: "",
    sex: "",
    status: "ACTIVE" as VariantStatus,
    tank_number: "",
    quantity: 0,
    reserved_quantity: 0
  });
  const [error, setError] = useState("");

  async function create(event: FormEvent) {
    event.preventDefault();
    setError("");
    const response = await fetch("/api/admin/variants", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        product_id: productId,
        name: draft.name,
        sku: draft.sku,
        price_cents: centsFromInput(draft.price),
        fish_size: draft.fish_size || null,
        length_min: draft.length_min ? Number(draft.length_min) : null,
        length_max: draft.length_max ? Number(draft.length_max) : null,
        sex: draft.sex || null,
        status: draft.status,
        tank_number: draft.tank_number || null,
        quantity: draft.quantity,
        reserved_quantity: draft.reserved_quantity
      })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setDraft({ name: "", sku: "", price: "", fish_size: "", length_min: "", length_max: "", sex: "", status: "ACTIVE", tank_number: "", quantity: 0, reserved_quantity: 0 });
    onCreated();
  }

  return (
    <form className="editor-card" onSubmit={create}>
      <h2>Add Variant</h2>
      {error ? <p className="error">{error}</p> : null}
      <div className="form-grid compact">
        <label>Name<input required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
        <label>SKU<input required value={draft.sku} onChange={(event) => setDraft({ ...draft, sku: event.target.value })} /></label>
        <label>Price<input required min="0" step="0.01" type="number" value={draft.price} onChange={(event) => setDraft({ ...draft, price: event.target.value })} /></label>
        <label>Size<input value={draft.fish_size} onChange={(event) => setDraft({ ...draft, fish_size: event.target.value })} /></label>
        <label>Min Length<input min="0" step="0.01" type="number" value={draft.length_min} onChange={(event) => setDraft({ ...draft, length_min: event.target.value })} /></label>
        <label>Max Length<input min="0" step="0.01" type="number" value={draft.length_max} onChange={(event) => setDraft({ ...draft, length_max: event.target.value })} /></label>
        <label>Sex<input value={draft.sex} onChange={(event) => setDraft({ ...draft, sex: event.target.value })} /></label>
        <label>Status<select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as VariantStatus })}>{variantStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
        <label>Tank Number<input value={draft.tank_number} onChange={(event) => setDraft({ ...draft, tank_number: event.target.value })} /></label>
        <label>Quantity<input min="0" type="number" value={draft.quantity} onChange={(event) => setDraft({ ...draft, quantity: event.target.valueAsNumber })} /></label>
        <label>Reserved<input min="0" type="number" value={draft.reserved_quantity} onChange={(event) => setDraft({ ...draft, reserved_quantity: event.target.valueAsNumber })} /></label>
      </div>
      <button type="submit">Create Variant</button>
    </form>
  );
}

function ImageManager({ productId, images, onReload }: { productId: string; images: ProductImage[]; onReload: () => void }) {
  const [draft, setDraft] = useState({ image_url: "", alt_text: "", sort_order: 0 });
  const [uploadDraft, setUploadDraft] = useState({ alt_text: "", sort_order: 0 });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);

  async function addImage(event: FormEvent) {
    event.preventDefault();
    setError("");
    const response = await fetch("/api/admin/product-images", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId, ...draft })
    });
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    setDraft({ image_url: "", alt_text: "", sort_order: 0 });
    onReload();
  }

  async function deleteImage(id: string) {
    await fetch(`/api/admin/product-images/${id}`, { method: "DELETE" });
    onReload();
  }

  async function uploadImage(event: FormEvent) {
    event.preventDefault();
    setUploadError("");

    if (!file) {
      setUploadError("Choose an image file to upload.");
      return;
    }

    const body = new FormData();
    body.append("product_id", productId);
    body.append("file", file);
    body.append("alt_text", uploadDraft.alt_text);
    body.append("sort_order", String(uploadDraft.sort_order));

    setUploading(true);
    const response = await fetch("/api/admin/uploads/product-image", {
      method: "POST",
      body
    });
    setUploading(false);

    if (!response.ok) {
      setUploadError(await readApiError(response));
      return;
    }

    setFile(null);
    setUploadDraft({ alt_text: "", sort_order: 0 });
    const fileInput = document.getElementById("product-image-upload") as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
    onReload();
  }

  return (
    <section className="section-block">
      <h2>Images</h2>
      {images.map((image) => (
        <div className="image-row" key={image.id}>
          <span>{image.sort_order}</span>
          <a href={image.image_url}>{image.image_url}</a>
          <span>{image.alt_text}</span>
          <button className="danger" onClick={() => deleteImage(image.id)} type="button">Delete</button>
        </div>
      ))}

      <form className="form-grid compact upload-form" onSubmit={uploadImage}>
        <h3 className="span-2">Upload Image</h3>
        {uploadError ? <p className="error span-2">{uploadError}</p> : null}
        <label className="span-2">
          Image File
          <input
            accept="image/jpeg,image/png,image/webp,image/gif"
            id="product-image-upload"
            type="file"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>
        <label>
          Alt Text
          <input
            value={uploadDraft.alt_text}
            onChange={(event) => setUploadDraft({ ...uploadDraft, alt_text: event.target.value })}
          />
        </label>
        <label>
          Sort Order
          <input
            min="0"
            type="number"
            value={uploadDraft.sort_order}
            onChange={(event) => setUploadDraft({ ...uploadDraft, sort_order: event.target.valueAsNumber })}
          />
        </label>
        <button disabled={uploading} type="submit">
          {uploading ? "Uploading" : "Upload Image"}
        </button>
      </form>

      <form className="form-grid compact" onSubmit={addImage}>
        <h3 className="span-2">Add Image URL</h3>
        {error ? <p className="error span-2">{error}</p> : null}
        <label className="span-2">Image URL<input required value={draft.image_url} onChange={(event) => setDraft({ ...draft, image_url: event.target.value })} /></label>
        <label>Alt Text<input value={draft.alt_text} onChange={(event) => setDraft({ ...draft, alt_text: event.target.value })} /></label>
        <label>Sort Order<input min="0" type="number" value={draft.sort_order} onChange={(event) => setDraft({ ...draft, sort_order: event.target.valueAsNumber })} /></label>
        <button type="submit">Add Image</button>
      </form>
    </section>
  );
}

function AdminPagesPage({ user }: { user: AdminUser }) {
  const [settings, setSettings] = useState<StorefrontSettings>(defaultStorefrontSettings);
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch("/api/admin/storefront-settings").then((response) => response.json() as Promise<StorefrontSettingsResponse>),
      fetch("/api/admin/categories").then((response) => response.json() as Promise<CategoriesResponse>)
    ])
      .then(([settingsData, categoriesData]) => {
        setSettings(settingsData.settings);
        setCategories(categoriesData.categories);
      })
      .catch(() => setError("Storefront settings could not be loaded."));
  }, []);

  function updateCategorySlug(oldSlug: string, nextSlug: string) {
    const order = settings.homepage_category_order.map((slug) => (slug === oldSlug ? nextSlug : slug));
    setSettings({ ...settings, homepage_category_order: order });
  }

  function moveCategory(index: number, direction: -1 | 1) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= settings.homepage_category_order.length) return;
    const order = [...settings.homepage_category_order];
    const current = order[index];
    order[index] = order[nextIndex];
    order[nextIndex] = current;
    setSettings({ ...settings, homepage_category_order: order });
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    const response = await fetch("/api/admin/storefront-settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings)
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    const data = (await response.json()) as StorefrontSettingsResponse;
    setSettings(data.settings);
    setMessage("Storefront settings saved.");
  }

  const categoryChoices = storefrontCategories.map((category) => ({ slug: category.slug, name: category.name }));

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Pages</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      {message ? <p className="success">{message}</p> : null}
      <form className="section-block" onSubmit={save}>
        <section className="editor-card">
          <h2>Homepage Content</h2>
          <div className="form-grid">
            <label className="span-2">
              Homepage blurb
              <textarea
                value={settings.homepage_blurb}
                onChange={(event) => setSettings({ ...settings, homepage_blurb: event.target.value })}
              />
            </label>
            <label>
              Homepage logo size
              <input
                min="240"
                max="1100"
                type="number"
                value={settings.homepage_logo_size}
                onChange={(event) => setSettings({ ...settings, homepage_logo_size: event.target.valueAsNumber || 900 })}
              />
            </label>
            <label>
              UPS image URL
              <input
                value={settings.homepage_ups_image_url}
                onChange={(event) => setSettings({ ...settings, homepage_ups_image_url: event.target.value })}
              />
            </label>
            <label>
              Rotation interval seconds
              <input
                min="3"
                max="60"
                type="number"
                value={settings.category_image_rotation_interval}
                onChange={(event) =>
                  setSettings({ ...settings, category_image_rotation_interval: event.target.valueAsNumber || 8 })
                }
              />
            </label>
            <label className="checkbox-label span-2">
              <input
                checked={settings.category_image_rotation_enabled === 1}
                type="checkbox"
                onChange={(event) =>
                  setSettings({ ...settings, category_image_rotation_enabled: event.target.checked ? 1 : 0 })
                }
              />
              Enable category image rotation
            </label>
          </div>
        </section>

        <section className="editor-card">
          <h2>Homepage Categories</h2>
          <p className="notice">
            Current D1 categories available: {categories.map((category) => category.name).join(", ") || "none yet"}.
          </p>
          <div className="editor-list">
            {settings.homepage_category_order.map((slug, index) => {
              const category = storefrontCategories.find((item) => item.slug === slug) ?? storefrontCategories[0];
              return (
                <div className="storefront-setting-row" key={`${slug}-${index}`}>
                  <label>
                    Category
                    <select value={slug} onChange={(event) => updateCategorySlug(slug, event.target.value)}>
                      {categoryChoices.map((item) => (
                        <option key={item.slug} value={item.slug}>{item.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="checkbox-label">
                    <input
                      checked={settings.homepage_category_visibility[slug] !== false}
                      type="checkbox"
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          homepage_category_visibility: {
                            ...settings.homepage_category_visibility,
                            [slug]: event.target.checked
                          }
                        })
                      }
                    />
                    Visible
                  </label>
                  <label>
                    Image URL
                    <input
                      placeholder={category.images[0]}
                      value={settings.homepage_category_images[slug] ?? ""}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          homepage_category_images: {
                            ...settings.homepage_category_images,
                            [slug]: event.target.value
                          }
                        })
                      }
                    />
                  </label>
                  <div className="actions">
                    <button className="secondary" onClick={() => moveCategory(index, -1)} type="button">Up</button>
                    <button className="secondary" onClick={() => moveCategory(index, 1)} type="button">Down</button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <button disabled={saving} type="submit">{saving ? "Saving" : "Save Page Settings"}</button>
      </form>
    </main>
  );
}

function EditProductPage({ productId, user }: { productId: string; user: AdminUser }) {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadProduct = () => {
    Promise.all([
      fetch(`/api/admin/products/${productId}`).then((response) => response.json() as Promise<AdminProductResponse>),
      fetch("/api/admin/categories").then((response) => response.json() as Promise<CategoriesResponse>)
    ])
      .then(([productData, categoriesData]) => {
        setProduct(productData.product);
        setCategories(categoriesData.categories);
      });
  };

  useEffect(loadProduct, [productId]);

  async function saveProduct(value: { name: string; slug: string; description: string; category_id: string; status: ProductStatus }) {
    setSaving(true);
    setError("");
    const response = await fetch(`/api/admin/products/${productId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...value, category_id: value.category_id || null })
    });
    setSaving(false);
    if (!response.ok) {
      setError(await readApiError(response));
      return;
    }
    loadProduct();
  }

  if (!product) return <main className="page">Loading product...</main>;

  return (
    <main className="page wide">
      <header className="page-header">
        <div>
          <p className="eyebrow">Admin Products</p>
          <h1>{product.name}</h1>
        </div>
        <AdminNav user={user} />
      </header>
      {error ? <p className="error">{error}</p> : null}
      <section className="section-block">
        <h2>Product Details</h2>
        <ProductForm
          categories={categories}
          initial={{
            name: product.name,
            slug: product.slug,
            description: product.description ?? "",
            category_id: product.category?.id ?? "",
            status: product.status
          }}
          onSubmit={saveProduct}
          saving={saving}
        />
      </section>
      <section className="section-block">
        <h2>Variants</h2>
        <div className="editor-list">
          {product.variants.map((variant) => (
            <VariantEditor key={variant.id} variant={variant} onReload={loadProduct} />
          ))}
        </div>
        <NewVariantForm productId={product.id} onCreated={loadProduct} />
      </section>
      <ImageManager productId={product.id} images={product.images} onReload={loadProduct} />
    </main>
  );
}

export function App() {
  const pathname = usePathname();

  if (pathname === "/admin/login") return <LoginPage />;
  if (pathname === "/admin/inventory") return <AuthGate>{(user) => <AdminInventoryPage user={user} />}</AuthGate>;
  if (pathname === "/admin/products") return <AuthGate>{(user) => <AdminProductsPage user={user} />}</AuthGate>;
  if (pathname === "/admin/products/new") return <AuthGate>{(user) => <NewProductPage user={user} />}</AuthGate>;
  if (pathname === "/admin/categories") return <AuthGate>{(user) => <AdminCategoriesPage user={user} />}</AuthGate>;
  if (pathname === "/admin/delivery-dates") {
    return <AuthGate>{(user) => <AdminDeliveryDatesPage user={user} />}</AuthGate>;
  }
  if (pathname === "/admin/shipping") return <AuthGate>{(user) => <AdminShippingPage user={user} />}</AuthGate>;
  if (pathname === "/admin/settings") return <AuthGate>{(user) => <AdminSettingsPage user={user} />}</AuthGate>;
  if (pathname === "/admin/pages" || pathname === "/admin/storefront") {
    return <AuthGate>{(user) => <AdminPagesPage user={user} />}</AuthGate>;
  }
  if (pathname === "/admin/orders") return <AuthGate>{(user) => <AdminOrdersPage user={user} />}</AuthGate>;
  if (pathname === "/admin/packing") return <AuthGate>{(user) => <AdminPackingPage user={user} />}</AuthGate>;
  if (pathname === "/admin/email-templates") return <AuthGate>{(user) => <AdminEmailTemplatesPage user={user} />}</AuthGate>;
  if (pathname.startsWith("/admin/orders/")) {
    return (
      <AuthGate>
        {(user) => <AdminOrderDetailPage orderId={pathname.replace("/admin/orders/", "")} user={user} />}
      </AuthGate>
    );
  }
  if (pathname.startsWith("/admin/products/")) {
    return (
      <AuthGate>
        {(user) => <EditProductPage productId={pathname.replace("/admin/products/", "")} user={user} />}
      </AuthGate>
    );
  }
  if (pathname === "/cart") return <><CartPage /><StoreFooter /></>;
  if (pathname === "/checkout") return <><CheckoutPage /><StoreFooter /></>;
  if (pathname === "/orders") return <><OrdersPage /><StoreFooter /></>;
  if (pathname.startsWith("/orders/")) return <><CustomerOrderDetailPage orderId={pathname.replace("/orders/", "")} /><StoreFooter /></>;
  if (pathname.startsWith("/category/")) return <><CategoryPage slug={pathname.replace("/category/", "")} /><StoreFooter /></>;
  if (pathname.startsWith("/product/")) return <><ProductPage slug={pathname.replace("/product/", "")} /><StoreFooter /></>;

  return <><HomePage /><StoreFooter /></>;
}
